import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { OverlayModule } from '@angular/cdk/overlay';
import { interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { HttpEventType } from '@angular/common/http';
import { DataSourceGet } from '../../models/datasource';
import {
  DataProfileResponse,
  ProfileTable,
  ColumnSummary,
  DataProfileGet,
  ColumnStats,
  ColumnBasicSummary,
  ColumnIssue,
  Finding,
  DateGap,
  ColumnMissingAnalysis,
  FullAssessmentColumnProfile,
  FullAssessmentColumnScoreSummary,
  FullAssessmentIssue,
  FullAssessmentMetadata,
  FullAssessmentSelectedPrimaryKey,
  TableIntentStepData,
  getProfileStepResult
} from '../../models/data-profile';

interface AssessmentInput {
  id: string;
  name: string;
  type: 'file' | 'datasource';
  fileName?: string;
}

interface AssessmentTableView {
  id: string;
  name: string;
  profile: ProfileTable;
}
import { FileUploadService, UploadedDataInfo } from '../../services/file-upload.service';
import { FloatingBasketComponent, BasketItem } from '../../components/floating-basket/floating-basket.component';
import { DataService, ProfilingStepOption } from '../../services/data.service';
import { LayoutService } from '../../services/layout.service';
import { DialogService } from '../../services/dialog.service';
import { VisualiseDialogComponent } from '../../components/visualise-dialog/visualise-dialog.component';
import { NewDataSourceDialogComponent } from '../../components/new-data-source-dialog/new-data-source-dialog.component';
import { DataSourcePickerComponent } from '../../components/data-source-picker/data-source-picker.component';
import { DataSummaryComponent, DataSummary } from '../../components/data-summary/data-summary.component';
import { DatasourceNameConflictDialogComponent, DatasourceConflictData, DatasourceConflictResult } from '../../components/datasource-name-conflict-dialog/datasource-name-conflict-dialog.component';
import { ProjectCreate } from '../../models/project/project-create.model';
import { DataSourceCreate } from '../../models/datasource/datasource-create.model';
import { RenameDialogComponent, RenameDialogData } from '../../components/rename-dialog/rename-dialog.component';
import { GoalCatalogGet } from '../../models/goal/goal-catalog-get.model';
import { GoalCreate } from '../../models/goal/goal-create.model';
import { TaskCreate } from '../../models/task/task-create.model';
import { TaskCatalogGet } from '../../models/task/task-catalog-get.model';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

interface TableFieldData {
  field: string;
  dataType: string;
  dataCategory: string;
  minimum: string | number;
  maximum: string | number;
  missing: number;
  unique: number;
  mean: string | number;
  mode: string | number;
  stdDev: string | number;
  percentile25: string | number;
  median: string | number;
  percentile75: string | number;
  sum: string | number;
  skewness: string | number;
  nullPercentage: number;
  distinctPercentage: number;
  chartImage?: string; // Base64 encoded chart image
  // String-specific length stats
  minLength?: number;
  maxLength?: number;
  valueCounts?: Record<string, number>;
  // Numeric values for positioning datetime box plots
  q1Numeric?: number;
  medianNumeric?: number;
  q3Numeric?: number;
  minNumeric?: number;
  maxNumeric?: number;
  // Distribution data for histograms
  distribution?: number[];
  distributionLabels?: string[];
  // Data quality issues
  issues?: ColumnIssue[];
  issueDataAvailable?: boolean;
  assessmentScore?: FullAssessmentColumnScoreSummary;
  assessmentProfile?: FullAssessmentColumnProfile;
  // Missing value analysis
  missingAnalysis?: ColumnMissingAnalysis;
}

@Component({
  selector: 'app-data-source-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatExpansionModule,
    MatTableModule,
    MatCardModule,
    MatChipsModule,
    MatTooltipModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSnackBarModule,
    OverlayModule,
    DataSummaryComponent,
    FloatingBasketComponent,
    DataSourcePickerComponent
  ],
  templateUrl: './data-source-profile.component.html',
  styleUrls: ['./data-source-profile.component.scss']
})
export class DataSourceProfileComponent implements OnInit, OnDestroy {
  dataSourceId: string | null = null;
  dataSource: DataSourceGet | null = null;
  profileData: DataProfileResponse | null = null;
  assessmentTables: AssessmentTableView[] = [];
  selectedAssessmentTableId: string | null = null;
  dataProfileRunId: string | null = null; // The actual profile run ID for renaming
  loading = false;
  error: string | null = null;
  profileName: string = ''; // Name for the data profile
  viewMode: 'generate' | 'existing' = 'generate'; // Track which mode we're in
  uploadedFileInfo: UploadedDataInfo | null = null; // Store uploaded file info for datasource creation
  uploadedAssessmentFiles: UploadedDataInfo[] = [];
  isSavingProfile = false; // Track when saving profile after datasource creation
  
  // Upload progress tracking
  uploadProgress = 0;
  uploadedBytes = 0;
  totalBytes = 0;
  uploadStatusText = 'Uploading file...';
  isUploading = false;
  
  // Table details section data
  numberOfRows: number | null = null;
  numberOfColumns: number | null = null;
  fileSize: string | null = null;
  numberOfDuplicates: number | null = null;
  predictedType: string | null = null;
  predictedTopic: string | null = null;
  primaryKey: string | null = null;
  selectedPrimaryKey: FullAssessmentSelectedPrimaryKey | null = null;
  recommendedActions: string[] = [];
  tableIntent: TableIntentStepData | null = null;

  // Step availability controls which result sections can be shown.
  hasColumnBasicStats = false;
  hasTableBasicStats = false;
  hasTableAdvancedStats = false;
  hasFullAssessment = false;
  hasSummaryData = false;
  hasTableIntent = false;
  
  // Data quality scores
  overallQualityScore: number | null = null;
  validityScore: number | null = null;
  distributionalHealthScore: number | null = null;
  completenessScore: number | null = null;
  forecastScore: number | null = null;
  freshnessScore: number | null = null;
  avgCorrelationScore: number | null = null;
  dateGaps: DateGap[] = [];
  continuityDate: string | null = null;
  
  // Table data
  tableFields: TableFieldData[] = [];
  
  // Data summary
  dataSummary: DataSummary | null = null;
  
  // Column selection state
  selectedColumnName: string | null = null;

  // Right sidebar collapse state - start collapsed
  isRightSidebarCollapsed = true;

  // Basket state
  basketItems: BasketItem[] = [];
  selectedIssueIds = new Set<string>();
  totalIssueCount: number = 0;
  // Store findings with recommendations for workflow creation
  findingsMap: Map<string, Finding> = new Map(); // key: columnName_issueId -> Finding
  
  // Workflow creation state
  creatingWorkflow: boolean = false;
  workflowProgress: { current: number; total: number } | null = null;
  
  // Goal catalog for checking recommendation availability
  goalCatalog: GoalCatalogGet[] = [];
  goalCatalogLoaded: boolean = false;

  // Expose Math to template
  Math = Math;

  // Assessment configuration UI state
  showConfig: boolean = true; // whether to show the master/detail configuration UI
  configOptions: ProfilingStepOption[] = [];
  selectedConfigIds: Set<string> = new Set<string>();
  profilingStepsLoading = true;
  configParameters: { [key: string]: any } = {};
  assessmentInputs: AssessmentInput[] = [];
  showAssessmentDataSourceMenu = false;
  showAssessmentDataSourceList = false;
  assessmentDataSourceSearchQuery = '';
  assessmentDataSources: DataSourceGet[] = [];
  isLoadingAssessmentDataSources = false;
  // View mode subscription
  private viewModeSubscription?: Subscription;
  private pollingSubscription: Subscription | null = null;
  loadingMessage: string = '';
  profilingCurrentStep: number | null = null;
  profilingTotalSteps: number | null = null;
  fetchingProfilingResults = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fileUploadService: FileUploadService,
    private dataService: DataService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    public layoutService: LayoutService,
    private location: Location,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.dataSourceId = this.route.snapshot.paramMap.get('datasourceId');
    const viewParam = this.route.snapshot.queryParamMap.get('view');
    const nameParam = this.route.snapshot.queryParamMap.get('name');
    this.viewMode = viewParam === 'existing' ? 'existing' : 'generate';
    this.profileName = nameParam || '';
    this.initializeAssessmentInputs();
    this.loadProfilingSteps();
    
    // Subscribe to view mode changes to update summary
    this.viewModeSubscription = this.layoutService.viewMode$.subscribe(() => {
      // Regenerate data summary when view mode changes
      if (this.profileData) {
        this.generateDataSummary();
      }
    });
    
    // Do not automatically start assessment on page load.
    // Show the configuration master/detail UI and wait for user to click "Start Assessment".
  }

  private loadProfilingSteps(): void {
    this.dataService.getProfilingSteps().subscribe({
      next: (options) => {
        this.configOptions = options;
        const defaultOptions = options.filter(option => option.isDefault);
        const selectedOptions = defaultOptions.length > 0 ? defaultOptions : options;
        this.selectedConfigIds = new Set(selectedOptions.map(option => option.id));
        this.selectedConfigIds.forEach(id => this.configParameters[id] = { param: '' });
        this.profilingStepsLoading = false;
      },
      error: (error) => {
        this.profilingStepsLoading = false;
        this.error = 'Unable to load assessment options.';
        console.error('Unable to load profiling steps:', error);
      }
    });
  }

  private initializeAssessmentInputs(): void {
    const navigationState = window.history.state as {
      assessmentDataSources?: Array<Pick<DataSourceGet, 'dataSourceId' | 'dataSourceName' | 'dataSourceTypeId'>>;
    };
    const navigationDataSources = navigationState.assessmentDataSources?.filter(dataSource => !!dataSource.dataSourceId);

    if (navigationDataSources?.length) {
      this.dataSourceId = navigationDataSources[0].dataSourceId || this.dataSourceId;
      this.assessmentInputs = navigationDataSources.map(dataSource => ({
        id: dataSource.dataSourceId!,
        name: dataSource.dataSourceName || 'Selected data source',
        type: 'datasource'
      }));
      return;
    }

    if (this.dataSourceId === '0') {
      const uploadedData = this.fileUploadService.getUploadedData();
      this.uploadedFileInfo = uploadedData;
      this.uploadedAssessmentFiles = uploadedData ? [uploadedData] : [];
      this.assessmentInputs = [{
        id: '0',
        name: uploadedData?.fileName || 'Uploaded file',
        fileName: uploadedData?.fileName,
        type: 'file'
      }];
      return;
    }

    this.assessmentInputs = [{
      id: this.dataSourceId || 'primary',
      name: this.profileName || 'Selected data source',
      type: 'datasource'
    }];

    if (this.dataSourceId && this.viewMode !== 'existing') {
      this.dataService.getDataSource(this.dataSourceId).subscribe({
        next: (dataSource) => {
          this.dataSource = dataSource;
          const primaryInput = this.assessmentInputs[0];
          if (primaryInput) {
            primaryInput.name = dataSource.dataSourceName;
          }
        },
        error: (error) => console.warn('Unable to load datasource name for assessment setup:', error)
      });
    }
  }

  private initializeAssessmentTables(response: DataProfileResponse): void {
    const tables = response.profile?.tables;
    if (!tables) {
      this.assessmentTables = [];
      this.selectedAssessmentTableId = null;
      return;
    }

    const inputNames = new Map(this.assessmentInputs.map(input => [input.id.replace(/-/g, ''), input.name]));
    this.assessmentTables = Object.entries(tables).map(([id, profile]) => ({
      id,
      name: inputNames.get(id) || id,
      profile
    }));

    this.resolveAssessmentTableNames();

    if (!this.selectedAssessmentTableId || !tables[this.selectedAssessmentTableId]) {
      this.selectedAssessmentTableId = this.assessmentTables[0]?.id || null;
    }
  }

  private resolveAssessmentTableNames(): void {
    const unresolvedTableIds = this.assessmentTables
      .filter(table => table.name === table.id)
      .map(table => table.id);

    if (unresolvedTableIds.length === 0) {
      return;
    }

    this.dataService.getDataSourceList().subscribe({
      next: (dataSources) => {
        const dataSourceNames = new Map(
          dataSources
            .filter(dataSource => !!dataSource.dataSourceId && !!dataSource.dataSourceName)
            .map(dataSource => [dataSource.dataSourceId!, dataSource.dataSourceName])
        );

        this.assessmentTables = this.assessmentTables.map(table => ({
          ...table,
          name: dataSourceNames.get(table.id) || table.name
        }));
      },
      error: (error) => console.warn('Unable to resolve datasource names for assessment results:', error)
    });
  }

  get selectedAssessmentTable(): AssessmentTableView | null {
    return this.assessmentTables.find(table => table.id === this.selectedAssessmentTableId) || null;
  }

  selectAssessmentTable(tableId: string): void {
    if (this.showConfig || tableId === this.selectedAssessmentTableId) {
      return;
    }

    this.selectedAssessmentTableId = tableId;
    this.selectedColumnName = null;
    this.selectedIssueIds.clear();
    this.basketItems = [];

    if (!this.profileData || !this.selectedAssessmentTable) {
      return;
    }

    if (this.dataSourceId === '0' && this.uploadedFileInfo) {
      this.processProfileData(this.profileData, this.uploadedFileInfo);
    } else if (this.dataSource) {
      this.processProfileDataFromDataSource(this.profileData, this.dataSource);
    }
  }

  private getSelectedProfile(response: DataProfileResponse): ProfileTable {
    this.initializeAssessmentTables(response);
    return this.selectedAssessmentTable?.profile || response.profile;
  }

  /**
   * Toggle a config option selection
   */
  toggleConfig(id: string, checked: boolean): void {
    if (checked) {
      this.selectedConfigIds.add(id);
      this.configParameters[id] = this.configParameters[id] || { param: '' };
    } else {
      this.selectedConfigIds.delete(id);
    }
  }

  /**
   * Return the selected option objects
   */
  getSelectedOptions() {
    return this.configOptions.filter(o => this.selectedConfigIds.has(o.id));
  }

  getConfigDescription(option: { simpleDescription: string; advancedDescription: string }): string {
    return this.layoutService.isAdvancedMode() ? option.advancedDescription : option.simpleDescription;
  }

  private generateAssessmentName(): string {
    const now = new Date();
    const timestamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0')
    ].join('-') + ` ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

    const baseName = this.assessmentInputs.length > 1
      ? 'Data Assessment'
      : this.assessmentInputs[0]?.name || 'Data Assessment';

    return `${baseName} (${timestamp})`;
  }

  cancelConfig(): void {
    this.goBack();
  }

  /**
   * Start assessment after configuration — calls the existing load methods.
   */
  startAssessment(): void {
    if (!this.profileName && this.viewMode !== 'existing') {
      this.profileName = this.generateAssessmentName();
    }

    // Hide configuration UI and begin the appropriate assessment flow
    this.showConfig = false;
    // Ensure any previous errors are cleared
    this.error = null;

    if (this.viewMode === 'existing') {
      this.loadExistingProfile();
    } else if (this.assessmentInputs.length > 0) {
      this.startConfiguredAssessment();
    } else {
      // No datasource id available - show error to user
      this.error = 'No data source ID provided in route. Cannot start assessment.';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      console.error('Attempted to start assessment but no data source ID found');
    }
  }

  addDataSource(): void {
    this.showAssessmentDataSourceMenu = !this.showAssessmentDataSourceMenu;
    this.showAssessmentDataSourceList = false;
  }

  private startConfiguredAssessment(): void {
    const files = this.uploadedAssessmentFiles
      .map(uploadedData => uploadedData.originalFile)
      .filter((file): file is File => !!file);
    const datasourceIds = this.assessmentInputs
      .filter(input => input.type === 'datasource')
      .map(input => input.id);
    const firstInput = this.assessmentInputs[0];

    if (this.uploadedAssessmentFiles.length !== files.length) {
      this.error = 'One or more uploaded files are no longer available. Please select them again.';
      this.snackBar.open(this.error, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
      this.showConfig = true;
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Starting Assessment';
    this.profilingCurrentStep = null;
    this.profilingTotalSteps = null;
    this.error = null;
    this.uploadStatusText = 'Uploading files...';
    this.isUploading = files.length > 0;
    this.totalBytes = files.reduce((total, file) => total + file.size, 0);
    this.uploadedBytes = 0;

    const nameToUse = this.profileName || this.generateAssessmentName();
    const steps = Array.from(this.selectedConfigIds).map(type => ({ type }));

    this.dataService.postDataProfileWithProgress(files, datasourceIds, undefined, nameToUse, steps).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total) {
            this.totalBytes = event.total;
            this.uploadedBytes = event.loaded;
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          }
          return;
        }

        if (event.type === HttpEventType.Response) {
          this.uploadProgress = 100;
          this.isUploading = false;
          this.pollAssessmentResult(event.body as string, firstInput);
        }
      },
      error: (error) => {
        this.error = `Failed to start data assessment: ${error.message || 'Unknown error'}`;
        this.snackBar.open(this.error, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
        this.loading = false;
        this.isUploading = false;
      }
    });
  }

  private pollAssessmentResult(actionToken: string, firstInput: AssessmentInput): void {
    this.loadingMessage = 'Assessment in Progress';
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = interval(3000).pipe(
      switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
      takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
    ).subscribe({
      next: status => {
        if (status.status === 2) {
          this.fetchingProfilingResults = true;
          this.dataService.getLongRunningActionResult(actionToken).subscribe({
            next: result => {
              const profileJson = result.profileResult?.profileJson;
              if (!profileJson) {
                this.error = 'No assessment result returned from server';
                this.loading = false;
                this.fetchingProfilingResults = false;
                return;
              }

              this.dataProfileRunId = result.profileResult?.dataSourceId ?? null;
              const profileResponse = JSON.parse(profileJson) as DataProfileResponse;
              this.profileData = profileResponse;
              const source = firstInput.type === 'datasource'
                ? this.dataSource || {
                    dataSourceId: firstInput.id,
                    dataSourceName: firstInput.name,
                    dataSourceTypeId: 0,
                    companyId: '',
                    schemaJson: '',
                    createdAt: new Date(),
                    updatedAt: new Date()
                  } as DataSourceGet
                : undefined;
              this.dataSource = source || this.dataSource;
              if (source) {
                this.processProfileDataFromDataSource(profileResponse, source);
              } else {
                const firstFile = this.uploadedAssessmentFiles[0];
                if (firstFile) {
                  this.processProfileData(profileResponse, firstFile);
                }
              }
              this.loading = false;
              this.fetchingProfilingResults = false;
              this.isRightSidebarCollapsed = false;
            },
            error: error => {
              this.error = `Failed to retrieve data assessment result: ${error.message || 'Unknown error'}`;
              this.loading = false;
              this.fetchingProfilingResults = false;
            }
          });
        } else if (status.status === 3 || status.status === 4) {
          this.error = status.errorMessage || 'Data assessment failed';
          this.loading = false;
        } else {
          this.profilingCurrentStep = status.currentStep ?? null;
          this.profilingTotalSteps = status.totalSteps ?? null;
        }
      },
      error: error => {
        this.error = `Failed to process data assessment: ${error.message || 'Unknown error'}`;
        this.loading = false;
      }
    });
  }

  selectExistingAssessmentDataSource(): void {
    this.assessmentDataSourceSearchQuery = '';
    this.showAssessmentDataSourceMenu = false;
    this.showAssessmentDataSourceList = true;
    this.isLoadingAssessmentDataSources = true;

    this.dataService.getDataSourceList().subscribe({
      next: (dataSources) => {
        this.assessmentDataSources = dataSources.filter(dataSource => dataSource.dataSourceTypeId !== 1);
        this.isLoadingAssessmentDataSources = false;
      },
      error: () => {
        this.assessmentDataSources = [];
        this.isLoadingAssessmentDataSources = false;
      }
    });
  }

  get filteredAssessmentDataSources(): DataSourceGet[] {
    const query = this.assessmentDataSourceSearchQuery.trim().toLowerCase();
    if (!query) {
      return this.assessmentDataSources;
    }

    return this.assessmentDataSources.filter(dataSource =>
      dataSource.dataSourceName?.toLowerCase().includes(query)
    );
  }

  get assessmentDataSourceIds(): string[] {
    return this.assessmentInputs
      .filter(input => input.type === 'datasource')
      .map(input => input.id);
  }

  selectAssessmentDataSource(dataSource: DataSourceGet): void {
    const dataSourceId = dataSource.dataSourceId;
    if (!dataSourceId || this.assessmentInputs.some(input => input.id === dataSourceId)) {
      return;
    }

    this.assessmentInputs.push({
      id: dataSourceId,
      name: dataSource.dataSourceName || 'Data source',
      type: 'datasource'
    });
    this.showAssessmentDataSourceList = false;
    this.assessmentDataSourceSearchQuery = '';
  }

  uploadAssessmentFile(): void {
    this.fileUploadService.openFileDialog().then(uploadedData => {
      if (!uploadedData) {
        return;
      }

      this.uploadedAssessmentFiles.push(uploadedData);
      this.assessmentInputs.push({
        id: `file-${Date.now()}`,
        name: uploadedData.fileName,
        fileName: uploadedData.fileName,
        type: 'file'
      });
    }).catch(error => {
      this.snackBar.open(error?.message || 'Unable to upload file', '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    });
  }

  isAssessmentDataSourceSelected(dataSourceId: string | undefined): boolean {
    return !!dataSourceId && this.assessmentInputs.some(input => input.id === dataSourceId);
  }

  createAssessmentDataSource(): void {
    this.showAssessmentDataSourceMenu = false;
    this.showAssessmentDataSourceList = false;
    const dialogRef = this.dialog.open(NewDataSourceDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: {
        title: 'New Data Source',
        mode: 'create',
        showExistingDataSources: false,
        featureContext: 'data-profile'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      const dataSourceId = result?.data?.dataSourceId;
      if (!dataSourceId || this.assessmentInputs.some(input => input.id === dataSourceId)) {
        return;
      }

      this.dataService.getDataSource(dataSourceId).subscribe({
        next: (dataSource) => this.assessmentInputs.push({
          id: dataSource.dataSourceId || dataSourceId,
          name: dataSource.dataSourceName,
          type: 'datasource'
        }),
        error: () => this.assessmentInputs.push({
          id: dataSourceId,
          name: result.data.name || result.data.dataSourceName || 'Data source',
          type: 'datasource'
        })
      });
    });
  }

  removeAssessmentInput(inputId: string): void {
    if (this.assessmentInputs.length <= 1) {
      return;
    }

    this.assessmentInputs = this.assessmentInputs.filter(input => input.id !== inputId);
    this.uploadedAssessmentFiles = this.uploadedAssessmentFiles.filter(file =>
      this.assessmentInputs.some(input => input.fileName === file.fileName)
    );
  }

  ngOnDestroy(): void {
    if (this.viewModeSubscription) {
      this.viewModeSubscription.unsubscribe();
    }
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }



  loadUploadedData(): void {
    this.loading = true;
    this.loadingMessage = 'Starting Assessment';
    this.profilingCurrentStep = null;
    this.profilingTotalSteps = null;
    this.error = null;
    this.uploadProgress = 0;
    this.uploadedBytes = 0;
    this.uploadStatusText = 'Uploading file...';

    const uploadedData = this.uploadedFileInfo || this.fileUploadService.getUploadedData();
    
    if (!uploadedData) {
      this.error = 'No uploaded data found. Please upload a file first.';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      this.loading = false;
      console.error('No uploaded data available for assessment view');
      return;
    }

    // Store uploaded file info for datasource creation
    this.uploadedFileInfo = uploadedData;

    // Check for original file
    if (!uploadedData.originalFile) {
      this.error = 'Original file not available. Please re-upload the file.';
      this.loading = false;
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Determine the profile name to use
    let nameToUse = this.profileName;
    if (!nameToUse && uploadedData.fileName) {
      nameToUse = uploadedData.fileName;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.totalBytes = uploadedData.originalFile.size;

    // Call the API endpoint with the original file — multipart/form-data with progress tracking
    this.dataService.postDataProfileWithProgress(uploadedData.originalFile, undefined, undefined, nameToUse).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total) {
            this.totalBytes = event.total;
            this.uploadedBytes = event.loaded;
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          }
        } else if (event.type === HttpEventType.Response) {
          this.uploadProgress = 100;
          this.isUploading = false;
          this.uploadStatusText = 'Processing assessment...';
          const actionToken = event.body as string;
          this.loadingMessage = 'Assessment in Progress';
          this.profilingCurrentStep = null;
          this.profilingTotalSteps = null;
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
          }
          this.pollingSubscription = interval(3000).pipe(
            switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
            takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
          ).subscribe({
            next: (status) => {
              if (status.status === 2) {
                setTimeout(() => {
                this.fetchingProfilingResults = true;
                this.dataService.getLongRunningActionResult(actionToken).subscribe({
                  next: (result) => {
                    const profileJson = result.profileResult?.profileJson;
                    if (!profileJson) {
                      this.error = 'No assessment result returned from server';
                      this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                      this.loading = false;
                      this.fetchingProfilingResults = false;
                      return;
                    }
                    // Store the profile run ID for renaming
                    this.dataProfileRunId = result.profileResult?.dataSourceId ?? null;
                    const apiResponse: DataProfileResponse = JSON.parse(profileJson);
                    this.profileData = apiResponse;
                    this.processProfileData(apiResponse, uploadedData);
                    this.loading = false;
                    this.fetchingProfilingResults = false;
                    this.isRightSidebarCollapsed = false;
                  },
                  error: (error) => {
                    console.error('Failed to fetch assessment result:', error);
                    this.error = `Failed to retrieve data assessment result: ${error.message || 'Unknown error'}`;
                    this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                    this.loading = false;
                    this.fetchingProfilingResults = false;
                  }
                });
                }, 3000);
              } else if (status.status === 3) {
                const errorMessage = status.errorMessage || 'Data assessment failed';
                this.error = errorMessage;
                this.snackBar.open(errorMessage, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                this.loading = false;
              } else if (status.status === 4) {
                this.error = 'Data assessment completed but the results could not be saved. Please try again.';
                this.snackBar.open(this.error, '×', { duration: 8000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                this.loading = false;
              } else {
                this.profilingCurrentStep = status.currentStep ?? null;
                this.profilingTotalSteps = status.totalSteps ?? null;
              }
            },
            error: (error) => {
              console.error('Polling error:', error);
              this.error = `Failed to process data assessment: ${error.message || 'Unknown error'}`;
              this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
              this.loading = false;
            }
          });
        }
      },
      error: (error) => {
        console.error('Upload failed:', error);
        this.error = `Failed to upload file: ${error.message || 'Unknown error'}`;
        this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
        this.loading = false;
        this.isUploading = false;
      }
    });
  }

  loadExistingProfile(): void {
    if (!this.dataSourceId) {
      this.error = 'No data source ID available';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Loading Assessment';
    this.error = null;

    // Load the existing stored profile using GET endpoint
    this.dataService.getDataProfile(this.dataSourceId).subscribe({
      next: (profileGet: DataProfileGet) => {
        console.log('Existing assessment loaded:', profileGet);
        
        // Store the profile run ID for renaming (the route param when view=existing is the profile run ID)
        this.dataProfileRunId = this.dataSourceId;
        
        // UPDATE: Store the actual datasource ID for use in visualizations
        // The route parameter might be a profile ID, but we need the actual datasource ID
        this.dataSourceId = profileGet.dataSourceId;
        
        // Parse the ProfileJson string to get the full profile data
        let parsedJson: any;
        try {
          parsedJson = JSON.parse(profileGet.profileJson);
        } catch (error) {
          console.error('Failed to parse assessment JSON:', error);
          this.error = 'Failed to parse data assessment';
          this.snackBar.open(this.error, '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
          this.loading = false;
          return;
        }
        
        // The profile data is nested inside a "profile" property
        // The stored JSON is the complete DataProfileResponse
        let profileResponse: DataProfileResponse;
        if (parsedJson.profile) {
          // If it has a profile property, it's the full DataProfileResponse
          profileResponse = parsedJson;
        } else {
          // If not, assume parsedJson is already the full response
          profileResponse = parsedJson;
        }
        
        // Create a DataSourceGet object from the profile metadata
        this.dataSource = {
          dataSourceId: profileGet.dataSourceId,
          dataSourceName: profileGet.dataSourceName,
          dataSourceTypeId: 0, // Unknown type
          companyId: '',
          schemaJson: '',
          createdAt: profileGet.createdAt,
          updatedAt: profileGet.updatedAt
        } as DataSourceGet;
        
        // Store the profile data
        this.profileData = profileResponse;
        
        // Fetch the data source data if we have a valid dataSourceId
        if (profileGet.dataSourceId && this.dataSource) {
          this.dataService.getDataSourceData(profileGet.dataSourceId).subscribe({
            next: (dataSourceData: any) => {
              console.log('Data source data loaded for existing assessment:', dataSourceData);
              
              // Process the response with the data
              this.processProfileDataFromDataSource(profileResponse, this.dataSource!, dataSourceData);
              
              this.loading = false;
              // Expand sidebar once profile is loaded
              this.isRightSidebarCollapsed = false;
            },
            error: (error) => {
              console.warn('Failed to load data source data, processing without sample data:', error);
              // Process without the sample data
              this.processProfileDataFromDataSource(profileResponse, this.dataSource!);
              
              this.loading = false;
              // Expand sidebar once profile is loaded
              this.isRightSidebarCollapsed = false;
            }
          });
        } else {
          // No dataSourceId available, process without sample data
          this.processProfileDataFromDataSource(profileResponse, this.dataSource!);
          
          this.loading = false;
          // Expand sidebar once profile is loaded
          this.isRightSidebarCollapsed = false;
        }
      },
      error: (error) => {
        console.error('Failed to load existing assessment:', error);
        this.error = `Failed to load existing assessment: ${error.message || 'Unknown error'}`;
        this.snackBar.open(this.error, '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
      }
    });
  }

  loadDataSourceProfile(): void {
    if (!this.dataSourceId) {
      this.error = 'No data source ID available';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Starting Assessment';
    this.profilingCurrentStep = null;
    this.profilingTotalSteps = null;
    this.error = null;
    this.uploadStatusText = 'Ingesting file...';

    // First, load the data source details
    this.dataService.getDataSource(this.dataSourceId).subscribe({
      next: (dataSource: DataSourceGet) => {
        this.dataSource = dataSource;
        console.log('Data source loaded:', dataSource);
        
        // Determine the profile name to use
        let nameToUse = this.profileName;
        if (!nameToUse && dataSource.dataSourceName) {
          nameToUse = dataSource.dataSourceName;
        }
        
        // Load the actual data from the datasource for the grid
        this.dataService.getDataSourceData(this.dataSourceId!).subscribe({
          next: (dataSourceData: any) => {
            console.log('Data source data loaded:', dataSourceData);
            
            // Call the API endpoint with the dataSourceId in the route — returns an action token
            this.dataService.postDataProfile(undefined, this.dataSourceId!, undefined, nameToUse).subscribe({
              next: (actionToken: string) => {
                this.loadingMessage = 'Assessment in Progress';
                this.profilingCurrentStep = null;
                this.profilingTotalSteps = null;
                if (this.pollingSubscription) {
                  this.pollingSubscription.unsubscribe();
                }
                this.pollingSubscription = interval(3000).pipe(
                  switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
                  takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
                ).subscribe({
                  next: (status) => {
                    if (status.status === 2) {
                      setTimeout(() => {
                      this.fetchingProfilingResults = true;
                      this.dataService.getLongRunningActionResult(actionToken).subscribe({
                        next: (result) => {
                          const profileJson = result.profileResult?.profileJson;
                          if (!profileJson) {
                            this.error = 'No assessment result returned from server';
                            this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                            this.loading = false;
                            this.fetchingProfilingResults = false;
                            return;
                          }
                          // Store the profile run ID for renaming
                          this.dataProfileRunId = result.profileResult?.dataSourceId ?? null;
                          const apiResponse: DataProfileResponse = JSON.parse(profileJson);
                          this.profileData = apiResponse;
                          this.processProfileDataFromDataSource(apiResponse, dataSource, dataSourceData);
                          this.loading = false;
                          this.fetchingProfilingResults = false;
                          this.isRightSidebarCollapsed = false;
                        },
                        error: (error) => {
                          console.error('Failed to fetch assessment result:', error);
                          this.error = `Failed to retrieve data assessment result: ${error.message || 'Unknown error'}`;
                          this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                          this.loading = false;
                          this.fetchingProfilingResults = false;
                        }
                      });
                      }, 3000);
                    } else if (status.status === 3) {
                      const errorMessage = status.errorMessage || 'Data assessment failed';
                      this.error = errorMessage;
                      this.snackBar.open(errorMessage, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                      this.loading = false;
                    } else if (status.status === 4) {
                      this.error = 'Data assessment completed but the results could not be saved. Please try again.';
                      this.snackBar.open(this.error, '×', { duration: 8000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                      this.loading = false;
                    } else {
                      this.profilingCurrentStep = status.currentStep ?? null;
                      this.profilingTotalSteps = status.totalSteps ?? null;
                    }
                  },
                  error: (error) => {
                    console.error('Polling error:', error);
                    this.error = `Failed to process data assessment: ${error.message || 'Unknown error'}`;
                    this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                    this.loading = false;
                  }
                });
              },
              error: (error) => {
                console.error('Assessment API call failed:', error);
                this.error = `Failed to process data assessment: ${error.message || 'Unknown error'}`;
                this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                this.loading = false;
              }
            });
          },
          error: (error) => {
            console.error('Failed to load data source data:', error);
            this.error = `Failed to load data source data: ${error.message || 'Unknown error'}`;
            this.snackBar.open(this.error, '×', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.loading = false;
          }
        });
      },
      error: (error) => {
        console.error('Failed to load data source:', error);
        this.error = `Failed to load data source: ${error.message || 'Unknown error'}`;
        this.snackBar.open(this.error, '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
      }
    });
  }

  /**
   * Process the profile data response for an existing data source
   */
  private processProfileDataFromDataSource(response: DataProfileResponse, dataSource: DataSourceGet, dataSourceData?: any): void {
    const profile = this.getSelectedProfile(response);

    // Validate response structure
    if (!response || !profile || !profile.step_results) {
      console.error('Invalid assessment response: missing profile.step_results', response);
      this.error = 'Invalid data assessment response received from server';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.updateStepAvailability(profile);
    this.numberOfRows = null;
    this.numberOfColumns = null;
    this.fileSize = null;
    this.numberOfDuplicates = null;
    this.overallQualityScore = null;
    this.validityScore = null;
    this.distributionalHealthScore = null;
    this.completenessScore = null;
    this.forecastScore = null;
    this.avgCorrelationScore = null;
    this.selectedPrimaryKey = null;
    this.dataSummary = null;

    // Get table basic stats from step_results
    const tableBasicStep = getProfileStepResult(profile.step_results, 'table_basic_stats');
    const tableBasicStats = tableBasicStep?.data?.table_basic;
    if (tableBasicStats) {
      this.numberOfRows = tableBasicStats.total_rows ?? null;
      this.numberOfColumns = tableBasicStats.total_columns ?? null;
      this.numberOfDuplicates = tableBasicStats.duplicate_rows ?? null;
      this.primaryKey = tableBasicStats['primary_key(s)'] || 'None detected';
      
      // Format file size from memory_usage (bytes)
      if (tableBasicStats.memory_usage !== undefined) {
        this.fileSize = this.formatFileSize(tableBasicStats.memory_usage);
      }
    } else {
      console.warn('Missing table_basic_stats in profile response');
    }

    // Get table advanced stats from step_results
    const advancedStatsStep = getProfileStepResult(profile.step_results, 'table_advanced_stats');
    if (advancedStatsStep?.data?.table_advanced) {
      const advancedSummary = advancedStatsStep.data.table_advanced;
      this.dateGaps = advancedSummary.date_gaps ?? [];
      this.continuityDate = advancedSummary.continuity_date ?? null;
    } else {
      console.warn('Missing table_advanced_stats step in profile response');
      this.dateGaps = [];
      this.continuityDate = null;
    }

    const fullAssessmentStep = getProfileStepResult(profile.step_results, 'full_assesment');
    const fullAssessment = fullAssessmentStep?.data;
    console.log('Full assessment data:', fullAssessment);
    const dimensionScores = fullAssessment?.dimension_scores;
    const assessmentMetadata = fullAssessment?.full_report?.metadata;
    this.recommendedActions = fullAssessment?.full_report?.recommended_actions ?? [];
    this.selectedPrimaryKey = assessmentMetadata?.selected_primary_key ?? null;
    if (this.selectedPrimaryKey?.column_name) {
      this.primaryKey = this.selectedPrimaryKey.column_name;
    }
    console.log('Dimension scores:', dimensionScores);
    if (fullAssessment && Array.isArray(dimensionScores)) {
      this.overallQualityScore = fullAssessment.overall_score ?? null;
      this.validityScore = dimensionScores.find((score: any) => score.metric === 'validity')?.score ?? null;
      this.distributionalHealthScore = dimensionScores.find((score: any) => score.metric === 'distributional_health')?.score ?? null;
      this.completenessScore = dimensionScores.find((score: any) => score.metric === 'completeness')?.score ?? null;
    } else {
      console.warn('Missing full_assesment scores in profile response');
    }

    // Process column stats from step_results
    const columnBasicStep = getProfileStepResult(profile.step_results, 'column_basic_stats');
    const columnAdvancedStep = getProfileStepResult(profile.step_results, 'column_advanced_stats');
    const chartDataStep = getProfileStepResult(profile.step_results, 'chart_data');
    const findingsStep = getProfileStepResult(profile.step_results, 'findings');
    const missingAnalysisStep = getProfileStepResult(profile.step_results, 'missing_analysis');

    if (columnBasicStep?.data?.columns) {
      this.tableFields = Object.values(columnBasicStep.data.columns).map(column => 
        this.mapColumnStatsToTableField(
          column, 
          columnAdvancedStep?.data?.columns?.[column.column_name],
          chartDataStep?.data[column.column_name],
          findingsStep?.data[column.column_name],
          missingAnalysisStep?.data.columns?.[column.column_name],
          assessmentMetadata,
          fullAssessment?.full_report?.issues
        )
      );
      // Compute total available issues for select-all/count display
      this.updateTotalIssueCount();
    } else {
      console.warn('Missing column_basic_stats in assessment response');
      this.tableFields = [];
      this.updateTotalIssueCount();
    }

    // Auto-select all issues is now disabled - checkboxes start unchecked
    // this.autoSelectAllIssues();

    // Populate sample data from dataSourceData if available
    if (dataSourceData) {
    }
    
    // Predicted type and topic are no longer available in new structure
    this.predictedType = null;
    this.predictedTopic = null;
    
    // Generate data summary
    this.generateDataSummary();
    
    // Load goal catalog to check recommendation availability
    this.loadGoalCatalog();
  }

  /**
   * Process the profile data response and populate component properties
   */
  private processProfileData(response: DataProfileResponse, uploadedData: UploadedDataInfo): void {
    const profile = this.getSelectedProfile(response);

    // Create data source object for display
    this.dataSource = {
      dataSourceId: '0',
      dataSourceName: uploadedData.fileName,
      dataSourceTypeId: 1,
      companyId: 'uploaded-data',
      schemaJson: '',
      createdAt: uploadedData.uploadedAt,
      updatedAt: uploadedData.uploadedAt
    } as DataSourceGet;

    // Validate response structure
    if (!response || !profile || !profile.step_results) {
      console.error('Invalid assessment response: missing profile.step_results', response);
      this.error = 'Invalid data assessment response received from server';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.updateStepAvailability(profile);
    this.numberOfRows = null;
    this.numberOfColumns = null;
    this.fileSize = null;
    this.numberOfDuplicates = null;
    this.overallQualityScore = null;
    this.validityScore = null;
    this.distributionalHealthScore = null;
    this.completenessScore = null;
    this.forecastScore = null;
    this.avgCorrelationScore = null;
    this.selectedPrimaryKey = null;
    this.dataSummary = null;

    // Get table basic stats from step_results
    const tableBasicStep = getProfileStepResult(profile.step_results, 'table_basic_stats');
    const tableBasicStats = tableBasicStep?.data?.table_basic;
    if (tableBasicStats) {
      this.numberOfRows = tableBasicStats.total_rows ?? null;
      this.numberOfColumns = tableBasicStats.total_columns ?? null;
      this.numberOfDuplicates = tableBasicStats.duplicate_rows ?? null;
      this.primaryKey = tableBasicStats['primary_key(s)'] || 'None detected';
      
      // Format file size from memory_usage (bytes)
      if (tableBasicStats.memory_usage !== undefined) {
        this.fileSize = this.formatFileSize(tableBasicStats.memory_usage);
      }
    } else {
      console.warn('Missing table_basic_stats in profile response');
    }

    // Get table advanced stats from step_results
    const advancedStatsStep = getProfileStepResult(profile.step_results, 'table_advanced_stats');
    if (advancedStatsStep?.data?.table_advanced) {
      const advancedSummary = advancedStatsStep.data.table_advanced;
      this.completenessScore = this.convertToPercentage(advancedSummary.completeness);
      this.forecastScore = typeof advancedSummary.ts_predictive_quality === 'number'
        ? this.convertToPercentage(advancedSummary.ts_predictive_quality)
        : null;
      const avgCorr = advancedSummary.correlation_summary?.['avg_correlation'];
      this.avgCorrelationScore = this.convertToPercentage(typeof avgCorr === 'number' ? avgCorr : 0);
      this.freshnessScore = null; // Not in new structure
      this.dateGaps = advancedSummary.date_gaps ?? [];
      this.continuityDate = advancedSummary.continuity_date ?? null;
    } else {
      console.warn('Missing table_advanced_stats step in profile response');
      this.dateGaps = [];
      this.continuityDate = null;
    }

    const fullAssessmentStep = getProfileStepResult(profile.step_results, 'full_assesment');
    const fullAssessment = fullAssessmentStep?.data;
    const dimensionScores = fullAssessment?.full_report?.score?.dimension_scores;
    const assessmentMetadata = fullAssessment?.full_report?.metadata;
    this.recommendedActions = fullAssessment?.full_report?.recommended_actions ?? [];
    this.selectedPrimaryKey = assessmentMetadata?.selected_primary_key ?? null;
    if (this.selectedPrimaryKey?.column_name) {
      this.primaryKey = this.selectedPrimaryKey.column_name;
    }
    if (fullAssessment && Array.isArray(dimensionScores)) {
      this.overallQualityScore = fullAssessment.overall_score ?? null;
      this.validityScore = dimensionScores.find((score: any) => score.metric === 'validity')?.score ?? null;
      this.distributionalHealthScore = dimensionScores.find((score: any) => score.metric === 'distributional_health')?.score ?? null;
      this.completenessScore = dimensionScores.find((score: any) => score.metric === 'completeness')?.score ?? null;
    } else {
      console.warn('Missing full_assesment scores in profile response');
    }

    // Process column stats from step_results
    const columnBasicStep = getProfileStepResult(profile.step_results, 'column_basic_stats');
    const columnAdvancedStep = getProfileStepResult(profile.step_results, 'column_advanced_stats');
    const chartDataStep = getProfileStepResult(profile.step_results, 'chart_data');
    const findingsStep = getProfileStepResult(profile.step_results, 'findings');
    const missingAnalysisStep = getProfileStepResult(profile.step_results, 'missing_analysis');

    if (columnBasicStep?.data?.columns) {
      this.tableFields = Object.values(columnBasicStep.data.columns).map(column => 
        this.mapColumnStatsToTableField(
          column, 
          columnAdvancedStep?.data?.columns?.[column.column_name],
          chartDataStep?.data[column.column_name],
          findingsStep?.data[column.column_name],
          missingAnalysisStep?.data.columns?.[column.column_name],
          assessmentMetadata,
          fullAssessment?.full_report?.issues
        )
      );
      // Compute total available issues for select-all/count display
      this.updateTotalIssueCount();
    } else {
      console.warn('Missing column_basic_stats in profile response');
      this.tableFields = [];
      this.updateTotalIssueCount();
    }

    // Auto-select all issues is now disabled - checkboxes start unchecked
    // this.autoSelectAllIssues();
    
    // Predicted type and topic are no longer available in new structure
    this.predictedType = null;
    this.predictedTopic = null;
    
    // Generate data summary
    this.generateDataSummary();
    
    // Load goal catalog to check recommendation availability
    this.loadGoalCatalog();
  }

  /**
   * Map a ColumnSummary to TableFieldData for display
   */
  private mapColumnToTableField(column: ColumnSummary): TableFieldData {
    // Provide safe defaults if properties are missing
    const basic = column?.basic || {} as any;
    const advanced = column?.advanced || {} as any;

    // Determine data category - use 'generic' if confidence is below 50%
    let dataCategory = this.normalizeDataType(advanced.soft_dtype || 'Unknown');
    if (advanced.soft_dtype && advanced.soft_dtype_proba !== undefined) {
      const confidence = advanced.soft_dtype_proba;
      // Check if confidence is below 50% (handle both 0-1 range and 0-100 range)
      if (confidence < 50) {
        dataCategory = 'generic';
      }
    }

    const fieldData: TableFieldData = {
      field: basic.column_name || 'Unknown',
      dataType: this.normalizeDataType(basic.atomic_data_type || 'Unknown'),
      dataCategory: dataCategory,
      minimum: this.formatNumericValue(basic.min ?? basic.min_length),
      maximum: this.formatNumericValue(basic.max ?? basic.max_length),
      minLength: basic.min_length,
      maxLength: basic.max_length,
      valueCounts: (basic as ColumnBasicSummary).value_counts,
      missing: basic.null_count ?? 0,
      unique: basic.distinct_count ?? 0,
      mean: this.formatNumericValue(basic.mean),
      mode: this.formatNumericValue(basic.mode),
      stdDev: this.formatNumericValue(advanced.std_dev),
      percentile25: this.formatNumericValue(advanced.q25),
      median: this.formatNumericValue(basic.median),
      percentile75: this.formatNumericValue(advanced.q75),
      sum: this.formatNumericValue(basic.sum),
      skewness: advanced.skewness ?? 'N/A',
      nullPercentage: (basic.null_percentage ?? 0) * 100,
      distinctPercentage: (basic.distinct_percentage ?? 0) * 100
    };

    // Add numeric values for box plot positioning
    if (basic.min !== undefined && typeof basic.min === 'number') {
      fieldData.minNumeric = basic.min;
    } else if (basic.min !== undefined && typeof basic.min === 'string') {
      const ts = new Date(basic.min).getTime();
      if (!isNaN(ts)) fieldData.minNumeric = ts;
    }
    if (basic.max !== undefined && typeof basic.max === 'number') {
      fieldData.maxNumeric = basic.max;
    } else if (basic.max !== undefined && typeof basic.max === 'string') {
      const ts = new Date(basic.max).getTime();
      if (!isNaN(ts)) fieldData.maxNumeric = ts;
    }
    // For datetime columns, q25/q75 are stored as strings in basic stats
    if (basic.q25 !== undefined && typeof basic.q25 === 'string') {
      const ts = new Date(basic.q25).getTime();
      if (!isNaN(ts)) fieldData.q1Numeric = ts;
    } else if (advanced.q25 !== undefined && typeof advanced.q25 === 'number') {
      fieldData.q1Numeric = advanced.q25;
    }
    if (basic.median !== undefined && typeof basic.median === 'number') {
      fieldData.medianNumeric = basic.median;
    } else if (basic.median !== undefined && typeof basic.median === 'string') {
      const ts = new Date(basic.median).getTime();
      if (!isNaN(ts)) fieldData.medianNumeric = ts;
    }
    if (basic.q75 !== undefined && typeof basic.q75 === 'string') {
      const ts = new Date(basic.q75).getTime();
      if (!isNaN(ts)) fieldData.q3Numeric = ts;
    } else if (advanced.q75 !== undefined && typeof advanced.q75 === 'number') {
      fieldData.q3Numeric = advanced.q75;
    }

    // Add chart image if available (from column summary chart data)
    if ((column as any).chart && (column as any).chart.kde) {
      fieldData.chartImage = (column as any).chart.kde;
    }

    // Generate distribution if chart data is available
    if (column.chart && column.chart['bins'] && Array.isArray(column.chart['bins'])) {
      // Extract count values from bins array for distribution chart
      fieldData.distribution = column.chart['bins'].map((bin: any) => bin.count || 0);
    }

    return fieldData;
  }

  /**
   * Map column stats from new structure to TableFieldData
   */
  private mapColumnStatsToTableField(
    basicStats: any,
    advancedStats?: any,
    chartData?: any,
    findingsData?: any,
    missingAnalysisData?: any,
    assessmentMetadata?: FullAssessmentMetadata,
    assessmentIssues?: FullAssessmentIssue[]
  ): TableFieldData {
    // Determine data category - use 'generic' if confidence is below 65%
    let dataCategory = this.normalizeDataType(basicStats.soft_data_type || 'Unknown');
    if (basicStats.soft_data_type && basicStats.soft_data_type_prob !== undefined) {
      const confidence = basicStats.soft_data_type_prob;
      // Check if confidence is below 65% (handle both 0-1 range and 0-100 range)
      if (confidence < 0.65) {
        dataCategory = 'generic';
      }
    }

    const fieldData: TableFieldData = {
      field: basicStats.column_name || 'Unknown',
      dataType: this.normalizeDataType(basicStats.atomic_data_type || 'Unknown'),
      dataCategory: dataCategory,
      minimum: this.formatNumericValue(basicStats.min ?? basicStats.min_length),
      maximum: this.formatNumericValue(basicStats.max ?? basicStats.max_length),
      minLength: basicStats.min_length,
      maxLength: basicStats.max_length,
      valueCounts: basicStats.value_counts,
      missing: basicStats.null_count ?? 0,
      unique: basicStats.distinct_count ?? 0,
      mean: this.formatNumericValue(basicStats.mean),
      mode: this.formatNumericValue(basicStats.mode),
      stdDev: this.formatNumericValue(basicStats.std_dev ?? advancedStats?.std_dev),
      percentile25: this.formatNumericValue(basicStats.q25 ?? advancedStats?.q25),
      median: this.formatNumericValue(basicStats.median),
      percentile75: this.formatNumericValue(basicStats.q75 ?? advancedStats?.q75),
      sum: this.formatNumericValue(basicStats.sum),
      skewness: basicStats.skewness ?? advancedStats?.skewness ?? 'N/A',
      nullPercentage: (basicStats.null_percentage ?? 0) * 100,
      distinctPercentage: (basicStats.distinct_percentage ?? 0) * 100,
      assessmentProfile: assessmentMetadata?.column_profiles?.find(
        profile => profile.column_name === basicStats.column_name
      ),
      assessmentScore: assessmentMetadata?.column_score_summary?.find(
        score => score.column_name === basicStats.column_name
      )
    };

    // Add numeric values for box plot positioning
    if (basicStats.min !== undefined && typeof basicStats.min === 'number') {
      fieldData.minNumeric = basicStats.min;
    } else if (basicStats.min !== undefined && typeof basicStats.min === 'string') {
      const ts = new Date(basicStats.min).getTime();
      if (!isNaN(ts)) fieldData.minNumeric = ts;
    }
    if (basicStats.max !== undefined && typeof basicStats.max === 'number') {
      fieldData.maxNumeric = basicStats.max;
    } else if (basicStats.max !== undefined && typeof basicStats.max === 'string') {
      const ts = new Date(basicStats.max).getTime();
      if (!isNaN(ts)) fieldData.maxNumeric = ts;
    }
    if (basicStats.q25 !== undefined && typeof basicStats.q25 === 'number') {
      fieldData.q1Numeric = basicStats.q25;
    } else if (basicStats.q25 !== undefined && typeof basicStats.q25 === 'string') {
      const ts = new Date(basicStats.q25).getTime();
      if (!isNaN(ts)) fieldData.q1Numeric = ts;
    } else if (advancedStats?.q25 !== undefined && typeof advancedStats.q25 === 'number') {
      fieldData.q1Numeric = advancedStats.q25;
    } else if (advancedStats?.q25 !== undefined && typeof advancedStats.q25 === 'string') {
      const ts = new Date(advancedStats.q25).getTime();
      if (!isNaN(ts)) fieldData.q1Numeric = ts;
    }
    if (basicStats.median !== undefined && typeof basicStats.median === 'number') {
      fieldData.medianNumeric = basicStats.median;
    } else if (basicStats.median !== undefined && typeof basicStats.median === 'string') {
      const ts = new Date(basicStats.median).getTime();
      if (!isNaN(ts)) fieldData.medianNumeric = ts;
    }
    if (basicStats.q75 !== undefined && typeof basicStats.q75 === 'number') {
      fieldData.q3Numeric = basicStats.q75;
    } else if (basicStats.q75 !== undefined && typeof basicStats.q75 === 'string') {
      const ts = new Date(basicStats.q75).getTime();
      if (!isNaN(ts)) fieldData.q3Numeric = ts;
    } else if (advancedStats?.q75 !== undefined && typeof advancedStats.q75 === 'number') {
      fieldData.q3Numeric = advancedStats.q75;
    } else if (advancedStats?.q75 !== undefined && typeof advancedStats.q75 === 'string') {
      const ts = new Date(advancedStats.q75).getTime();
      if (!isNaN(ts)) fieldData.q3Numeric = ts;
    }

    // Add chart data from profile.chart_data (new structure)
    if (chartData && chartData.data && Array.isArray(chartData.data)) {
      // Extract count values from bins array for distribution chart
      fieldData.distribution = chartData.data.map((bin: any) => bin.count || 0);
    }

    const findingsIssues = findingsData?.findings && Array.isArray(findingsData.findings)
      ? this.mapFindingsToIssues(findingsData.findings, basicStats.total_rows, basicStats.column_name)
      : (basicStats.issues || advancedStats?.issues || []);
    const fullAssessmentIssues = assessmentIssues
      ? this.mapFullAssessmentIssues(
          assessmentIssues.filter(issue => issue.column === basicStats.column_name)
        )
      : [];

    // Findings and full-assessment issues are complementary checks.
    if ((findingsData !== undefined && findingsData !== null) || assessmentIssues !== undefined) {
      fieldData.issueDataAvailable = true;
      fieldData.issues = [...findingsIssues, ...fullAssessmentIssues];
    }

    // Map legacy issues only when no newer issue source was returned.
    if (fieldData.issues === undefined && (basicStats.issues || advancedStats?.issues)) {
      fieldData.issues = basicStats.issues || advancedStats.issues;
    }

    // Add missing analysis data
    if (missingAnalysisData) {
      fieldData.missingAnalysis = missingAnalysisData;
    }

    return fieldData;
  }

  private mapFullAssessmentIssues(issues: FullAssessmentIssue[]): ColumnIssue[] {
    return issues.map((issue, index) => ({
      id: `assessment-${issue.check_id}-${index}`,
      title: this.formatFindingCode(issue.check_id),
      severity: issue.severity === 'critical' || issue.severity === 'high' || issue.severity === 'error'
        ? 'high'
        : issue.severity === 'medium' || issue.severity === 'warning'
          ? 'medium'
          : 'low',
      description: issue.message,
      affectedRows: issue.failed_count,
      affectedPercentage: issue.total_count > 0 ? issue.frequency * 100 : undefined
    }));
  }

  /**
   * Format numeric value with appropriate significant figures
   * @param value The numeric value to format (can be string for date/time values)
   * @returns Formatted string or 'N/A' if value is null/undefined
   */
  formatNumericValue(value: number | string | null | undefined): string | number {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // If value is a string (e.g., date/time), return as-is
    if (typeof value === 'string') {
      return value;
    }

    // For integers or values close to integers, return as-is
    if (Number.isInteger(value) || Math.abs(value - Math.round(value)) < 0.0001) {
      return Math.round(value);
    }

    // For decimal values, use significant figures
    // If value is >= 1, show 3-4 significant figures
    if (Math.abs(value) >= 1) {
      return Number(value.toPrecision(4));
    }

    // For values < 1, show 3 significant figures
    return Number(value.toPrecision(3));
  }

  /**
   * Map API findings to UI-friendly issues and store in findings map
   */
  private mapFindingsToIssues(findings: Finding[], totalRows: number, columnName?: string): ColumnIssue[] {
    return findings.map((finding, index) => {
      // Map severity from API format to UI format
      let severity: 'low' | 'medium' | 'high';
      if (finding.severity === 'error') {
        severity = 'high';
      } else if (finding.severity === 'warning') {
        severity = 'medium';
      } else {
        severity = 'low';
      }

      // Generate a human-readable title from the code
      const title = this.formatFindingCode(finding.code);

      // Generate suggested fix from recommendations
      const suggestedFix = finding.recommendations && finding.recommendations.length > 0
        ? finding.recommendations.map(rec => this.formatRecommendation(rec)).join(', ')
        : undefined;

      // Calculate percentage
      const affectedPercentage = totalRows > 0 ? (finding.affected_count / totalRows) * 100 : 0;

      const issueId = `${finding.code}-${index}`;
      
      // Store the finding with recommendations for later use
      if (columnName) {
        const findingKey = `${columnName}_${issueId}`;
        this.findingsMap.set(findingKey, finding);
      }

      return {
        id: issueId,
        title: title,
        severity: severity,
        description: finding.reason,
        suggestedFix: suggestedFix,
        affectedRows: finding.affected_count,
        affectedPercentage: affectedPercentage
      };
    });
  }

  /**
   * Format finding code into human-readable title
   */
  private formatFindingCode(code: string): string {
    // Convert snake_case to Title Case
    return code
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Format recommendation code into human-readable text
   */
  private formatRecommendation(recommendation: string): string {
    // Remove 'clean.' prefix and convert to readable format
    const cleanRec = recommendation.replace(/^clean\./, '');
    return cleanRec
      .split('.')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Convert decimal to percentage and round to whole number (e.g., 0.9615 -> 96)
   */
  private convertToPercentage(value: number | null): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    // If value is already a percentage (> 1), round it
    // If value is a decimal (0-1), convert to percentage and round
    return value > 1 ? Math.round(value) : Math.round(value * 100);
  }

  /**
   * Calculate overall quality score from column-level statistics
   * Averages the overall_quality values from all columns
   */
  private calculateOverallQualityFromColumns(response: DataProfileResponse): number | null {
    const columnAdvancedStep = getProfileStepResult(response.profile?.step_results ?? [], 'column_advanced_stats');
    
    if (!columnAdvancedStep?.data?.columns) {
      return null;
    }
    
    const columns = Object.values(columnAdvancedStep.data.columns) as any[];
    const qualityScores = columns
      .map(col => col.overall_quality)
      .filter(score => score !== null && score !== undefined && typeof score === 'number');
    
    if (qualityScores.length === 0) {
      return null;
    }
    
    const average = qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length;
    return Math.round(average * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Calculate continuity score from column-level pk_continuity values
   * For primary key columns with pk_continuity = 100, the score is 100%
   * For other values, normalize them to a 0-100 scale
   */
  private calculateContinuityScore(response: DataProfileResponse): number | null {
    const columnAdvancedStep = getProfileStepResult(response.profile?.step_results ?? [], 'column_advanced_stats');
    
    if (!columnAdvancedStep?.data?.columns) {
      return null;
    }
    
    const columns = Object.values(columnAdvancedStep.data.columns) as any[];
    const continuityScores = columns
      .map(col => col.pk_continuity)
      .filter(score => score !== null && score !== undefined && typeof score === 'number');
    
    if (continuityScores.length === 0) {
      return null;
    }
    
    // If all scores are <= 100, they're already percentages, so average them
    const allArePercentages = continuityScores.every(score => score <= 100);
    if (allArePercentages) {
      const average = continuityScores.reduce((sum, score) => sum + score, 0) / continuityScores.length;
      return Math.round(average);
    }
    
    // If we have mixed values (some <= 100, some > 100), normalize the large values
    // Large values represent gap sizes - convert to a score where lower gaps = higher score
    const normalizedScores = continuityScores.map(score => {
      if (score <= 100) {
        return score; // Already a percentage
      } else {
        // For large gap values, convert to inverse score (lower gap = higher score)
        // Cap at a reasonable maximum gap size (e.g., 1,000,000) and invert
        const maxGap = 1000000;
        const normalizedGap = Math.min(score, maxGap);
        return 100 * (1 - (normalizedGap / maxGap));
      }
    });
    
    const average = normalizedScores.reduce((sum, score) => sum + score, 0) / normalizedScores.length;
    return Math.round(average);
  }

  /**
   * Format bytes to human-readable file size
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }











  getDataSourceTypeLabel(dataSourceTypeId: number): string {
    switch (dataSourceTypeId) {
      case 1: return 'API Listener';
      case 2: return 'API Retriever';
      default: return 'Unknown';
    }
  }

  goBack(): void {
    this.location.back();
  }

  editProfileName(): void {
    if (!this.dataProfileRunId) return;

    const currentName = this.profileName || this.dataSource?.dataSourceName || '';
    
    const dialogRef = this.dialog.open(RenameDialogComponent, {
      width: '520px',
      data: {
        currentName: currentName,
        itemType: 'data assessment'
      } as RenameDialogData
    });

    dialogRef.afterClosed().subscribe((newName: string | undefined) => {
      if (newName && newName !== this.profileName) {
        this.dataService.renameDataProfileRun(this.dataProfileRunId!, newName).subscribe({
          next: (success: boolean) => {
            if (success) {
              this.profileName = newName;
            }
          },
          error: (error) => {
            console.error('Error renaming profile:', error);
            this.snackBar.open(error.message || 'Failed to rename profile', '×', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    });
  }

  toggleRightSidebar(): void {
    this.isRightSidebarCollapsed = !this.isRightSidebarCollapsed;
  }

  /**
   * Check if currently profiling an uploaded file (not a datasource)
   */
  isProfilingUploadedFile(): boolean {
    return this.dataSourceId === '0' && this.uploadedFileInfo !== null;
  }

  /**
   * Open the New Data Source dialog with the uploaded file pre-populated
   */
  createDataSourceFromFile(): void {
    if (!this.uploadedFileInfo) {
      console.error('No uploaded file info available');
      return;
    }

    // Use the original file if available, otherwise reconstruct from parsed data
    let file: File;
    if (this.uploadedFileInfo.originalFile) {
      // Use the original raw file to preserve format and content
      file = this.uploadedFileInfo.originalFile;
      console.log('Using original file:', file.name, file.type);
    } else {
      // Fallback: reconstruct file from parsed data (legacy support)
      console.warn('Original file not available, reconstructing from parsed data');
      const jsonString = JSON.stringify(this.uploadedFileInfo.data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      file = new File([blob], this.uploadedFileInfo.fileName, { 
        type: 'application/json',
        lastModified: this.uploadedFileInfo.uploadedAt.getTime()
      });
    }

    const dialogRef = this.dialog.open(NewDataSourceDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: {
        title: 'Create Data Source',
        mode: 'create',
        initialStage: 'file-upload',
        preloadedFile: file,
        featureContext: 'data-profile'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data && result.data.dataSourceId) {
        console.log('Data source created:', result);
        // Start saving the profile with the new datasource
        this.isSavingProfile = true;
        this.dataSourceId = result.data.dataSourceId;
        this.assessmentInputs[0] = {
          id: result.data.dataSourceId,
          name: result.data.name || result.data.dataSourceName || 'Data source',
          type: 'datasource'
        };
        
        // Load the datasource and regenerate profile
        this.reloadProfileWithDataSource(result.data.dataSourceId);
      }
    });
  }

  /**
   * Reload the profile using the new datasource ID
   */
  private reloadProfileWithDataSource(dataSourceId: string): void {
    // First, load the datasource details
    this.dataService.getDataSource(dataSourceId).subscribe({
      next: (dataSource) => {
        this.dataSource = dataSource;
        
        // Determine the profile name to use
        let nameToUse = this.profileName;
        if (!nameToUse && dataSource.dataSourceName) {
          nameToUse = dataSource.dataSourceName;
        }
        
        // Now generate a new profile for this datasource — returns an action token
        this.dataService.postDataProfile(undefined, dataSourceId, undefined, nameToUse).subscribe({
          next: (actionToken: string) => {
            this.profilingCurrentStep = null;
            this.profilingTotalSteps = null;
            if (this.pollingSubscription) {
              this.pollingSubscription.unsubscribe();
            }
            this.pollingSubscription = interval(3000).pipe(
              switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
              takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
            ).subscribe({
              next: (status) => {
                if (status.status === 2) {
                  setTimeout(() => {
                  this.fetchingProfilingResults = true;
                  this.dataService.getLongRunningActionResult(actionToken).subscribe({
                    next: (result) => {
                      const profileJson = result.profileResult?.profileJson;
                      if (!profileJson) {
                        this.error = 'No assessment result returned from server';
                        this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                        this.isSavingProfile = false;
                        this.fetchingProfilingResults = false;
                        return;
                      }
                      // Store the profile run ID for renaming
                      this.dataProfileRunId = result.profileResult?.dataSourceId ?? null;
                      const apiResponse: DataProfileResponse = JSON.parse(profileJson);
                      this.profileData = apiResponse;
                      this.processProfileDataFromDataSource(apiResponse, dataSource);
                      this.uploadedFileInfo = null;
                      this.viewMode = 'generate';
                      this.isSavingProfile = false;
                      this.fetchingProfilingResults = false;
                      this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: {},
                        replaceUrl: true
                      });
                    },
                    error: (error: any) => {
                      console.error('Failed to fetch assessment result:', error);
                      this.error = `Failed to retrieve data assessment result: ${error.message || 'Unknown error'}`;
                      this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                      this.isSavingProfile = false;
                      this.fetchingProfilingResults = false;
                    }
                  });
                  }, 3000);
                } else if (status.status === 3) {
                  const errorMessage = status.errorMessage || 'Data assessment failed';
                  this.error = errorMessage;
                  this.snackBar.open(errorMessage, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                  this.isSavingProfile = false;
                } else if (status.status === 4) {
                  this.error = 'Data assessment completed but the results could not be saved. Please try again.';
                  this.snackBar.open(this.error, '×', { duration: 8000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                  this.isSavingProfile = false;
                } else {
                  this.profilingCurrentStep = status.currentStep ?? null;
                  this.profilingTotalSteps = status.totalSteps ?? null;
                }
              },
              error: (error: any) => {
                console.error('Polling error:', error);
                this.error = `Failed to save data assessment: ${error.message || 'Unknown error'}`;
                this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
                this.isSavingProfile = false;
              }
            });
          },
          error: (error: any) => {
            console.error('Failed to regenerate assessment:', error);
            this.error = `Failed to save data assessment: ${error.message || 'Unknown error'}`;
            this.snackBar.open(this.error!, '×', { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] });
            this.isSavingProfile = false;
          }
        });
      },
      error: (error: any) => {
        console.error('Failed to load datasource:', error);
        this.error = `Failed to load data source: ${error.message || 'Unknown error'}`;
        this.snackBar.open(this.error, '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.isSavingProfile = false;
      }
    });
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) {
      return 'N/A';
    }

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (!dateObj || isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  /**
   * Format datetime value as YYYY-MM-DD
   * @param value The datetime value to format (can be string or Date)
   * @returns Formatted string in YYYY-MM-DD format or original value if not a valid date
   */
  formatDateTimeValue(value: string | number | null | undefined): string | number {
    if (value === null || value === undefined || value === 'N/A') {
      return 'N/A';
    }

    // If it's a number, return as-is
    if (typeof value === 'number') {
      return value;
    }

    // Try to parse as date
    const dateObj = new Date(value);
    
    if (isNaN(dateObj.getTime())) {
      // Not a valid date, return original value
      return value;
    }

    // Format as YYYY-MM-DD
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  /**
   * Format a box plot value for datetime columns
   * Shows only date if values span multiple days, otherwise shows only time
   * @param value The datetime value to format (string or number)
   * @returns Formatted string showing date or time as appropriate
   */
  formatBoxPlotDateTimeValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === 'N/A') {
      return 'N/A';
    }

    const fieldData = this.getSelectedFieldData();
    if (!fieldData || !this.isDateTimeType(fieldData.dataType)) {
      return String(value);
    }

    // Check if all values are within the same day
    const allSameDay = this.areAllDateTimeValuesOnSameDay();
    
    // Parse the value as a date
    const dateObj = new Date(value);
    if (isNaN(dateObj.getTime())) {
      return String(value);
    }

    if (allSameDay) {
      // Show only time (HH:MM:SS)
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      const seconds = String(dateObj.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    } else {
      // Show only date (YYYY-MM-DD)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }

  /**
   * Check if all datetime values in the box plot are on the same day
   * @returns true if all values are on the same day, false otherwise
   */
  private areAllDateTimeValuesOnSameDay(): boolean {
    const fieldData = this.getSelectedFieldData();
    if (!fieldData || !fieldData.minNumeric || !fieldData.maxNumeric) {
      return false;
    }

    // Convert numeric timestamps to dates
    const minDate = new Date(fieldData.minNumeric);
    const maxDate = new Date(fieldData.maxNumeric);

    // Check if they're on the same day
    return (
      minDate.getFullYear() === maxDate.getFullYear() &&
      minDate.getMonth() === maxDate.getMonth() &&
      minDate.getDate() === maxDate.getDate()
    );
  }

  /**
   * Get tooltip text for quality scores
   * @param scoreType Type of quality score
   * @param score Percentage score (0-100) or null
   * @returns Tooltip description text
   */
  getQualityScoreTooltip(scoreType: string, score: number | null): string {
    if (score === null) {
      return 'Data assessment metrics are not yet available.';
    }
    
    const level = score >= 75 ? 'high' : score >= 50 ? 'medium' : 'low';
    
    switch (scoreType.toLowerCase()) {
      case 'completeness':
        return level === 'high' 
          ? 'Indicates that most data fields are populated'
          : level === 'medium'
            ? 'Indicates that some rows contain missing data'
            : 'Indicates that many rows contain missing data';
      case 'forecast':
        return level === 'high'
          ? 'Indicates excellent predictive capability'
          : level === 'medium'
            ? 'Indicates moderate forecasting potential'
            : 'Indicates limited ability to forecast future trends';
      case 'uniqueness':
        return level === 'high'
          ? 'Indicates most data values are distinct'
          : level === 'medium'
            ? 'Indicates moderate data duplication'
            : 'Indicates significant data duplication';
      case 'correlation':
      case 'avgcorrelation':
        return level === 'high'
          ? 'Indicates strong relationships between data columns'
          : level === 'medium'
            ? 'Indicates moderate correlation between columns'
            : 'Indicates weak or no correlation between columns';
      case 'continuity':
        return level === 'high'
          ? 'Indicates minimal gaps in the time series'
          : level === 'medium'
            ? 'Indicates some gaps in the time series'
            : 'Indicates significant gaps in the time series';
      case 'freshness':
        return level === 'high'
          ? 'Indicates data is very recent and up-to-date'
          : level === 'medium'
            ? 'Indicates data is moderately recent'
            : 'Indicates data may be outdated';
      default:
        return 'Indicates the quality level of this data aspect';
    }
  }

  /**
   * Calculate speedometer needle rotation based on score
   * @param score Percentage score (0-100) or null
   * @returns Rotation in degrees (-90 to 90, representing 0% to 100%)
   */
  getSpeedometerRotation(score: number | null): number {
    if (score === null) {
      return -90; // Point to start (0%)
    }
    // Map 0-100 to -90 to 90 degrees
    return (score * 1.8) - 90;
  }

  /**
   * Determine which segment of the speedometer is active based on score
   * @param score Percentage score (0-100) or null
   * @returns Segment identifier: 'red', 'orange', 'yellow', 'green', or null
   */
  getActiveSpeedometerSegment(score: number | null): string | null {
    if (score === null) {
      return null;
    }
    if (score < 60) return 'red';
    if (score < 75) return 'orange';
    if (score < 90) return 'yellow';
    return 'green';
  }

  /**
   * Get the quality label text based on score
   * @param score Percentage score (0-100) or null
   * @returns Quality label: 'Poor', 'Moderate', 'Good', 'Excellent', or 'N/A'
   */
  getQualityLabel(score: number | null): string {
    if (score === null) {
      return 'N/A';
    }
    if (score < 60) return 'Poor';
    if (score < 75) return 'Moderate';
    if (score < 90) return 'Good';
    return 'Excellent';
  }

  /**
   * Get color hex code based on percentage score
   * Uses Material Design color palette for consistency
   * @param score Percentage score (0-100) or null
   * @returns Color hex code
   */
  getScoreColor(score: number | null): string {
    if (score === null) {
      return 'var(--charcoal)'; // No data
    }
    if (score >= 75) {
      return 'var(--success-green)'; // High performance
    } else if (score >= 50) {
      return 'var(--hot-orange)'; // Medium performance
    } else {
      return 'var(--brand-red)'; // Low performance
    }
  }

  /**
   * Get CSS class name based on percentage score
   * @param score Percentage score (0-100) or null
   * @returns CSS class name for the appropriate color
   */
  getScoreColorClass(score: number | null): string {
    if (score === null) {
      return 'score-none';
    }
    if (score >= 75) {
      return 'score-high';
    } else if (score >= 50) {
      return 'score-medium';
    } else {
      return 'score-low';
    }
  }

  /**
   * Get the maximum gap days value from the date_gaps array
   * Used for scaling the date gaps chart bars
   * @param gaps Array of DateGap objects
   * @returns Maximum gap_days value
   */
  getMaxGapDays(gaps: DateGap[]): number {
    if (!gaps || gaps.length === 0) {
      return 1;
    }
    return Math.max(...gaps.map(g => g.gap_days));
  }

  /**
   * Calculate the gap ratio for a date gap segment
   * Returns the gap density (gap_days / number of rows in segment)
   * @param gap DateGap object
   * @returns Gap ratio as a decimal (0-1)
   */
  getGapRatio(gap: DateGap): number {
    const segmentSize = gap.end_idx - gap.start_idx + 1;
    if (segmentSize <= 0) {
      return 0;
    }
    return gap.gap_days / segmentSize;
  }

  /**
   * Get the maximum gap ratio from all date gaps
   * Used for scaling the date gaps chart bars
   * @param gaps Array of DateGap objects
   * @returns Maximum gap ratio
   */
  getMaxGapRatio(gaps: DateGap[]): number {
    if (!gaps || gaps.length === 0) {
      return 1;
    }
    const ratios = gaps.map(g => this.getGapRatio(g));
    const maxRatio = Math.max(...ratios);
    return maxRatio > 0 ? maxRatio : 1;
  }

  /**
   * Get tooltip text with percentage and description based on score level
   * @param scoreType The type of score (e.g., 'completeness', 'forecast')
   * @param score The percentage score (0-100) or null
   * @returns Formatted tooltip text
   */
  getScoreTooltip(scoreType: string, score: number | null): string {
    if (score === null) {
      return `${scoreType} score not available - data assessment pending`;
    }

    const percentage = `${score}%`;
    let description = '';
    let explanation = '';

    // Get description based on score level
    if (score >= 75) {
      description = 'High';
    } else if (score >= 50) {
      description = 'Medium';
    } else {
      description = 'Low';
    }

    // Get specific explanation based on score type
    switch (scoreType.toLowerCase()) {
      case 'completeness':
        explanation = score >= 75 
          ? 'indicates that most data fields are populated'
          : score >= 50 
            ? 'indicates that some rows contain missing data'
            : 'indicates that many rows contain missing data';
        break;
      case 'forecast':
        explanation = score >= 75 
          ? 'indicates excellent predictive capability'
          : score >= 50 
            ? 'indicates moderate forecasting potential'
            : 'indicates limited ability to forecast future trends';
        break;
      case 'uniqueness':
        explanation = score >= 75 
          ? 'indicates most data values are distinct'
          : score >= 50 
            ? 'indicates moderate data duplication'
            : 'indicates significant data duplication';
        break;
      case 'continuity':
        explanation = score >= 75 
          ? 'indicates consistent data flow over time'
          : score >= 50 
            ? 'indicates some gaps in data continuity'
            : 'indicates significant gaps in data flow';
        break;
      case 'freshness':
        explanation = score >= 75 
          ? 'indicates data is very recent and up-to-date'
          : score >= 50 
            ? 'indicates data is moderately recent'
            : 'indicates data may be outdated';
        break;
      default:
        explanation = 'indicates the quality level of this data aspect';
        break;
    }

    return `${percentage} - A ${description} ${scoreType} Score ${explanation}`;
  }

  getScoreIcon(score: number | null): string {
    if (score === null) return 'help_outline';
    if (score >= 70) return 'check_circle';
    if (score >= 40) return 'warning';
    return 'error';
  }

  // Column selection methods (from data-detail component)
  onColumnSelectChange(columnName: string): void {
    if (columnName === 'table-summary') {
      this.selectedColumnName = null;
      // Collapse sidebar if it was expanded for column detail
      if (!this.isRightSidebarCollapsed) {
        // Keep it open to show table summary
      }
    } else {
      this.selectedColumnName = columnName;
      // Open the sidebar if it's collapsed and a column is selected
      if (this.isRightSidebarCollapsed) {
        this.isRightSidebarCollapsed = false;
      }
    }
  }

  onColumnHeaderClick(event: Event, columnName: string): void {
    // Ignore clicks on checkboxes and their wrappers
    const target = event.target as HTMLElement;
    if (target.closest('.checkbox-wrapper') || target.closest('mat-checkbox')) {
      return;
    }
    
    event.stopPropagation();
    this.selectedColumnName = this.selectedColumnName === columnName ? null : columnName;
    
    // Open the sidebar if it's collapsed and a column is selected
    if (this.selectedColumnName && this.isRightSidebarCollapsed) {
      this.isRightSidebarCollapsed = false;
    }
  }

  isColumnSelected(columnName: string): boolean {
    return this.selectedColumnName === columnName;
  }

  onTableClick(event: Event): void {
    // Check if the click was outside of a column header
    const target = event.target as HTMLElement;
    if (!target.closest('.metadata-header')) {
      this.selectedColumnName = null;
    }
  }

  closeColumnDetail(): void {
    this.selectedColumnName = null;
  }

  // Calculate distribution bar height as percentage
  getDistributionHeight(value: number, distribution: number[]): number {
    const max = Math.max(...distribution);
    return max > 0 ? (value / max) * 100 : 0;
  }

  // Generate SVG path for curved line over histogram
  getHistogramCurvePath(distribution: number[]): string {
    if (!distribution || distribution.length === 0) {
      return '';
    }

    const max = Math.max(...distribution);
    if (max === 0) {
      return '';
    }

    // Apply weighted average smoothing to the distribution values
    const smoothedDistribution = this.smoothDistribution(distribution);
    const smoothedMax = Math.max(...smoothedDistribution);

    // Use expanded coordinate space to accommodate curve overflow
    // Map bars from 10 to 100 (leaving 10 units at top for curve overflow)
    const topMargin = 10;
    const usableHeight = 90;
    const baseline = topMargin + usableHeight; // Y coordinate for zero height (100)

    // Calculate points based on bar positions using smoothed values
    const barWidth = 100 / distribution.length;
    const points: { x: number; y: number }[] = smoothedDistribution.map((value, index) => {
      const x = (index + 0.5) * barWidth; // Center of each bar
      const normalizedHeight = (value / smoothedMax) * usableHeight;
      const y = baseline - normalizedHeight; // Position from baseline upward
      return { x, y };
    });

    if (points.length === 0) {
      return '';
    }

    if (points.length === 1) {
      return `M ${points[0].x} ${points[0].y}`;
    }

    if (points.length === 2) {
      return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    }

    // Add anchor points at the baseline for first and last positions
    const firstX = barWidth * 0.5;
    const lastX = 100 - (barWidth * 0.5);
    
    // Start from baseline at first bar position
    let path = `M ${firstX} ${baseline}`;
    
    // Connect to first data point
    path += ` L ${points[0].x} ${points[0].y}`;

    // Use Catmull-Rom spline for a single smooth continuous curve through data points
    const tension = 0.5;

    for (let i = 0; i < points.length - 1; i++) {
      // Get the four points needed for Catmull-Rom calculation
      const p0 = i > 0 ? points[i - 1] : points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1];

      // Calculate control points for cubic bezier using Catmull-Rom
      const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
      const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
      
      const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
      const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    // Connect last data point to baseline
    path += ` L ${lastX} ${baseline}`;

    return path;
  }

  /**
   * Apply weighted average smoothing to distribution data
   * Each value is replaced by a weighted average of itself and its neighbors
   */
  private smoothDistribution(distribution: number[]): number[] {
    if (distribution.length <= 2) {
      return [...distribution];
    }

    const smoothed: number[] = [];
    
    for (let i = 0; i < distribution.length; i++) {
      if (i === 0) {
        // First element: weighted average with next value
        smoothed.push(distribution[i] * 0.6 + distribution[i + 1] * 0.4);
      } else if (i === distribution.length - 1) {
        // Last element: weighted average with previous value
        smoothed.push(distribution[i] * 0.6 + distribution[i - 1] * 0.4);
      } else {
        // Middle elements: weighted average with both neighbors
        smoothed.push(
          distribution[i - 1] * 0.25 + 
          distribution[i] * 0.5 + 
          distribution[i + 1] * 0.25
        );
      }
    }

    return smoothed;
  }

  // Calculate range label for histogram bins
  getRangeLabel(index: number, field: TableFieldData): string {
    if (typeof field.minimum !== 'number' || typeof field.maximum !== 'number') {
      return `Range ${index + 1}`;
    }

    const min = field.minimum;
    const max = field.maximum;
    const numBins = field.distribution?.length || 10;
    const binSize = (max - min) / numBins;
    
    const rangeStart = min + (index * binSize);
    const rangeEnd = min + ((index + 1) * binSize);

    // Format the values appropriately
    const formatValue = (val: number): string => {
      if (Number.isInteger(val) || Math.abs(val - Math.round(val)) < 0.0001) {
        return Math.round(val).toString();
      }
      return val.toFixed(2);
    };

    return `${formatValue(rangeStart)} to ${formatValue(rangeEnd)}`;
  }

  getSelectedFieldData(): TableFieldData | null {
    if (!this.selectedColumnName) {
      return null;
    }
    return this.tableFields.find(field => field.field === this.selectedColumnName) || null;
  }

  hasBoxPlotData(field: TableFieldData | null): boolean {
    if (!field || !this.isIQRApplicable(field.dataType)) {
      return false;
    }

    const hasQuartile = (value: string | number): boolean =>
      value !== 'N/A' && value !== null && value !== undefined;

    return hasQuartile(field.percentile25) && hasQuartile(field.percentile75);
  }

  getMainContentAvailabilityMessage(): string {
    if (!this.hasColumnBasicStats) {
      return 'No column-specific details are available. Enable Column Basic Statistics to view column details.';
    }

    return 'No column-specific details are available for the selected assessment steps.';
  }

  private updateStepAvailability(profile: ProfileTable): void {
    const stepResults = profile.step_results;
    const columnBasicStep = getProfileStepResult(stepResults, 'column_basic_stats');
    const tableBasicStep = getProfileStepResult(stepResults, 'table_basic_stats');
    const tableAdvancedStep = getProfileStepResult(stepResults, 'table_advanced_stats');
    const fullAssessmentStep = getProfileStepResult(stepResults, 'full_assesment');
    const summaryStep = getProfileStepResult(stepResults, 'summary_data');
    const tableIntentStep = getProfileStepResult(stepResults, 'table_intent');

    this.hasColumnBasicStats = !!columnBasicStep?.success && !!columnBasicStep.data?.columns;
    this.hasTableBasicStats = !!tableBasicStep?.success && !!tableBasicStep.data?.table_basic;
    this.hasTableAdvancedStats = !!tableAdvancedStep?.success && !!tableAdvancedStep.data?.table_advanced;
    this.hasFullAssessment = !!fullAssessmentStep?.success && !!fullAssessmentStep.data?.full_report;
    this.hasSummaryData = !!summaryStep?.success && !!summaryStep.data?.summary;
    this.hasTableIntent = !!tableIntentStep?.success && !!tableIntentStep.data;
    this.tableIntent = this.hasTableIntent ? tableIntentStep!.data : null;
  }

  getSelectedColumnInfo(): { chartImage?: string } | null {
    return this.getSelectedFieldData();
  }

  // Placeholder method for visualise functionality
  onVisualiseClick(): void {
    console.log('Visualise clicked for column:', this.selectedColumnName);
    
    // Determine if we're using a real data source or uploaded data
    const isUploadedData = this.dataSourceId === '0';
    
    const dialogRef = this.dialog.open(VisualiseDialogComponent, {
      width: '90vw',
      height: '85vh',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        columnName: this.selectedColumnName,
        columnInfo: this.getSelectedFieldData(),
        availableColumns: this.tableFields.map(f => f.field),
        availableColumnsMetadata: this.tableFields.map(f => ({ field: f.field, dataType: f.dataType })),
        dataSourceId: isUploadedData ? undefined : this.dataSourceId,
        uploadedData: isUploadedData ? this.fileUploadService.getUploadedData()?.data : undefined,
        hideXAxisSelector: true // Hide X-axis selector from Data Assessment screen
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Dialog was closed', result);
    });
  }

  // Type normalization and checking helper methods
  /**
   * Normalize data type names to ensure consistency
   * Converts 'bool' to 'boolean' to match existing UI logic
   */
  private normalizeDataType(dataType: string): string {
    if (dataType === 'bool') {
      return 'boolean';
    }
    return dataType;
  }

  isNumericType(dataType: string): boolean {
    return ['number', 'integer', 'float', 'decimal', 'currency', 'numeric', 'INTEGER', 'FLOAT'].includes(dataType);
  }

  isStringType(dataType: string): boolean {
    return !this.isNumericType(dataType) && !this.isDateTimeType(dataType) && dataType.toLowerCase() !== 'boolean';
  }

  getTopValueCounts(valueCounts: Record<string, number> | undefined, limit = 8): { value: string; percentage: number }[] {
    if (!valueCounts) return [];
    return Object.entries(valueCounts)
      .filter(([key]) => key !== '')
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([value, proportion]) => ({ value, percentage: proportion * 100 }));
  }

  isDateTimeType(dataType: string): boolean {
    return ['date', 'datetime', 'timestamp', 'DATE', 'DATETIME', 'TIMESTAMP'].includes(dataType);
  }

  isIQRApplicable(dataType: string): boolean {
    // IQR/Box plots are applicable for both numeric and datetime columns
    return this.isNumericType(dataType) || this.isDateTimeType(dataType);
  }

  // Helper methods to determine which statistics are applicable for different data types
  isStatisticApplicable(statistic: string, dataType: string): boolean {
    const lowerDataType = dataType.toLowerCase();
    
    switch (statistic) {
      case 'sum':
      case 'mean':
      case 'standardDeviation':
        // Only applicable for numeric types
        return this.isNumericType(dataType);
      
      case 'min':
      case 'max':
      case 'median':
      case 'q1':
      case 'q3':
        // Applicable for numeric and datetime types
        return this.isNumericType(dataType) || this.isDateTimeType(dataType);
      
      case 'mode':
        // Mode is applicable for all data types (most frequent value)
        return true;
      
      default:
        return true;
    }
  }

  // Box plot positioning methods (from data-detail component)
  getBoxPlotPosition(statistic: string): number {
    const fieldData = this.getSelectedFieldData();
    if (!fieldData) return 0;

    // Handle datetime columns using numeric values
    if (this.isDateTimeType(fieldData.dataType) && fieldData.minNumeric && fieldData.maxNumeric) {
      const min = fieldData.minNumeric;
      const max = fieldData.maxNumeric;
      const range = max - min;

      if (range === 0) return 50;

      let value: number;
      switch (statistic) {
        case 'q1':
          value = fieldData.q1Numeric || min;
          break;
        case 'median':
          value = fieldData.medianNumeric || ((min + max) / 2);
          break;
        case 'q3':
          value = fieldData.q3Numeric || max;
          break;
        default:
          return 0;
      }

      const position = ((value - min) / range) * 100;
      return Math.max(0, Math.min(100, position));
    }

    // Handle numeric columns (existing logic)
    if (typeof fieldData.minimum !== 'number' || typeof fieldData.maximum !== 'number') {
      return 0;
    }

    const min = fieldData.minimum;
    const max = fieldData.maximum;
    const range = max - min;

    if (range === 0) return 50; // Prevent division by zero

    let value: number;
    switch (statistic) {
      case 'q1':
        value = fieldData.percentile25 as number || min;
        break;
      case 'median':
        value = fieldData.median as number || ((min + max) / 2);
        break;
      case 'q3':
        value = fieldData.percentile75 as number || max;
        break;
      default:
        return 0;
    }

    // Convert value to percentage position within the range
    const position = ((value - min) / range) * 100;
    return Math.max(0, Math.min(100, position)); // Clamp between 0 and 100
  }

  /**
   * Determines if a boolean bar is too short and should have its label above it
   */
  isBooleanBarShort(value: number, distribution: number[]): boolean {
    const height = this.getDistributionHeight(value, distribution);
    return height < 25; // Less than 25% height
  }

  getMedianPosition(): number {
    const fieldData = this.getSelectedFieldData();
    if (!fieldData || typeof fieldData.minimum !== 'number' || typeof fieldData.maximum !== 'number') {
      return 50;
    }

    const q1Position = this.getBoxPlotPosition('q1');
    const q3Position = this.getBoxPlotPosition('q3');
    const medianGlobalPosition = this.getBoxPlotPosition('median');
    
    // Calculate median position relative to the box (Q1 to Q3)
    const boxWidth = q3Position - q1Position;
    if (boxWidth === 0) return 50;
    
    const medianRelativePosition = ((medianGlobalPosition - q1Position) / boxWidth) * 100;
    return Math.max(0, Math.min(100, medianRelativePosition));
  }

  // Helper method for formatting values in table cells
  formatValue(value: any): string {
    if (value === null || value === undefined) {
      return 'null';
    }
    return String(value);
  }

  // Data type color helper (from data-detail component)
  getDataTypeColor(dataType: string): string {
    switch (dataType.toLowerCase()) {
      case 'string':
      case 'text':
        return '#4CAF50'; // Green
      case 'number':
      case 'integer':
      case 'float':
      case 'decimal':
      case 'currency':
      case 'numeric':
        return '#2196F3'; // Blue
      case 'boolean':
        return '#FF9800'; // Orange
      case 'date':
      case 'datetime':
        return '#9C27B0'; // Purple
      default:
        return '#607D8B'; // Blue Grey
    }
  }

  // Data category icon helper
  getDataCategoryIcon(dataCategory: string): string {
    const category = dataCategory?.toLowerCase() || 'generic';
    
    switch (category) {
      case 'email':
        return 'assets/icons/email-white.svg';
      case 'currency':
        return 'assets/icons/currency-white.svg';
      case 'numeric':
        return 'assets/icons/number-white.svg';
      case 'ip_address':
        return 'assets/icons/ip-white.svg';
      case 'coordinates':
        return 'assets/icons/coordinate-white.svg';
      case 'natural_language':
        return 'assets/icons/naturallanguage-white.svg';
      case 'code_string':
        return 'assets/icons/code-white.svg';
      case 'country':
        return 'assets/icons/country-white.svg';
      case 'phone_number':
        return 'assets/icons/phone-white.svg';
      case 'address':
        return 'assets/icons/address-white.svg';
      case 'boolean':
        return 'assets/icons/boolean-white.svg';
      case 'date':
      case 'datetime':
        return 'assets/icons/date-white.svg';
      case 'temporal':
        return 'assets/icons/time-white.svg';
      case 'url':
        return 'assets/icons/Network world_-white.svg';
      case 'uuid':
        return 'assets/icons/uuid-white.svg';
      case 'three_words':
        return 'assets/icons/coordinate-white.svg';
      case 'language_code':
        return 'assets/icons/country-white.svg';
      case 'cryptocurrency':
        return 'assets/icons/label-white.svg';
      case 'generic':
      default:
        return 'assets/icons/label-white.svg';
    }
  }

  // Data category icon helper for sidebar (regular colored icons)
  getDataCategoryIconRegular(dataCategory: string): string {
    const category = dataCategory?.toLowerCase() || 'generic';
    
    switch (category) {
      case 'email':
        return 'assets/icons/email.svg';
      case 'currency':
        return 'assets/icons/currency.svg';
      case 'numeric':
        return 'assets/icons/number.svg';
      case 'ip_address':
        return 'assets/icons/ip.svg';
      case 'coordinates':
        return 'assets/icons/coordinate.svg';
      case 'natural_language':
        return 'assets/icons/naturallanguage.svg';
      case 'code_string':
        return 'assets/icons/code.svg';
      case 'country':
        return 'assets/icons/country.svg';
      case 'phone_number':
        return 'assets/icons/phone.svg';
      case 'address':
        return 'assets/icons/address.svg';
      case 'boolean':
        return 'assets/icons/boolean.svg';
      case 'date':
      case 'datetime':
        return 'assets/icons/date.svg';
      case 'temporal':
        return 'assets/icons/time.svg';
      case 'url':
        return 'assets/icons/Network world_.svg';
      case 'uuid':
        return 'assets/icons/uuid.svg';
      case 'three_words':
        return 'assets/icons/coordinate.svg';
      case 'language_code':
        return 'assets/icons/country.svg';
      case 'cryptocurrency':
        return 'assets/icons/label.svg';
      case 'generic':
      default:
        return 'assets/icons/label.svg';
    }
  }

  // Data category display name helper
  getDataCategoryDisplayName(dataCategory: string): string {
    const category = dataCategory?.toLowerCase() || 'generic';
    
    switch (category) {
      case 'email':
        return 'Email';
      case 'currency':
        return 'Currency';
      case 'numeric':
        return 'Numeric';
      case 'ip_address':
        return 'IP Address';
      case 'coordinates':
        return 'Coordinates';
      case 'natural_language':
        return 'Natural Language';
      case 'code_string':
        return 'Code String';
      case 'country':
        return 'Country';
      case 'phone_number':
        return 'Phone Number';
      case 'address':
        return 'Address';
      case 'boolean':
        return 'Boolean';
      case 'date':
      case 'datetime':
        return 'Date';
      case 'temporal':
        return 'Temporal';
      case 'url':
        return 'URL';
      case 'uuid':
        return 'UUID';
      case 'three_words':
        return 'What 3 Words';
      case 'language_code':
        return 'Language';
      case 'cryptocurrency':
        return 'Cryptocurrency';
      case 'generic':
      default:
        return 'Generic';
    }
  }

  /**
   * Generate data summary from profile data
   * Uses API-provided summary directly - no frontend generation
   */
  private generateDataSummary(): void {
    if (!this.profileData || !this.hasSummaryData) {
      this.dataSummary = null;
      return;
    }

    // The API returns summary and top_facts inside profile, not at the top level.
    // Support both locations for resilience.
    const profileLevel = this.getSelectedProfile(this.profileData) as any;
    const summaryContent: { basic?: any; advanced?: any } =
      profileLevel?.summary ?? (this.profileData.summary as any);

    if (!summaryContent?.basic && !summaryContent?.advanced) {
      console.warn('API did not provide summary data');
      this.dataSummary = null;
      return;
    }

    // Get the appropriate narrative based on selected view mode
    const currentViewMode = this.layoutService.getViewMode();
    const narrative =
      currentViewMode === 'advanced'
        ? (summaryContent.advanced || summaryContent.basic)
        : (summaryContent.basic || summaryContent.advanced);

    if (!narrative || (!narrative.bullets && !narrative.summary)) {
      console.warn('API summary is missing required fields');
      this.dataSummary = null;
      return;
    }

    // Convert top_facts to key_statistics format (may not always be present)
    const topFacts: [number, any][] = profileLevel?.top_facts ?? (this.profileData.summary as any)?.top_facts ?? [];
    const keyStatistics = topFacts.map(([importance, fact]) => {
      // Determine category based on metric or scope
      let category = 'general';
      if (fact.metric.includes('completeness') || fact.metric.includes('uniqueness') || 
          fact.metric.includes('null') || fact.metric.includes('duplicate')) {
        category = 'quality';
      } else if (fact.metric.includes('rows') || fact.metric.includes('columns')) {
        category = 'size';
      } else if (fact.metric.includes('predictive') || fact.metric.includes('forecast')) {
        category = 'analysis';
      }

      // Create human-readable description from metric
      let description = fact.metric
        .replace('advanced.', '')
        .replace('basic.', '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l: string) => l.toUpperCase());

      // Add column context if applicable
      if (fact.column) {
        description = `${description} (${fact.column})`;
      }

      // Format value
      let value: string;
      if (fact.metric.includes('percentage') || fact.metric.includes('completeness') || 
          fact.metric.includes('uniqueness') || fact.metric.includes('quality')) {
        value = `${fact.value.toFixed(1)}%`;
      } else if (fact.metric.includes('rows') || fact.metric.includes('columns')) {
        value = fact.value.toLocaleString();
      } else {
        value = fact.value.toString();
      }

      return {
        category: category,
        description: description,
        value: value,
        importance_score: importance,
        scope: fact.scope // Include scope from fact (TABLE or COLUMN)
      };
    });

    // Use API's bullets and summary directly
    this.dataSummary = {
      bullet_points: narrative.bullets ?? [],
      key_statistics: keyStatistics,
      overall_assessment: narrative.summary ?? '',
      generated_at: new Date().toISOString()
    };
  }

  getFileSizeString(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Basket methods
  onIssueCheckboxChange(columnName: string, issue: ColumnIssue, checked: boolean): void {
    const itemId = `${columnName}_${issue.id}`;
    
    if (checked) {
      this.selectedIssueIds.add(itemId);
      
      // Get the finding to access recommendations
      const finding = this.findingsMap.get(itemId);
      
      this.basketItems.push({
        id: itemId,
        columnName: columnName,
        issueTitle: issue.title,
        severity: issue.severity,
        recommendations: finding?.recommendations || []
      });
    } else {
      this.selectedIssueIds.delete(itemId);
      this.basketItems = this.basketItems.filter(item => item.id !== itemId);
    }
  }

  isIssueSelected(columnName: string, issueId: string): boolean {
    return this.selectedIssueIds.has(`${columnName}_${issueId}`);
  }

  hasRecommendations(columnName: string, issueId: string): boolean {
    const itemId = `${columnName}_${issueId}`;
    const finding = this.findingsMap.get(itemId);
    return finding?.recommendations && finding.recommendations.length > 0 || false;
  }

  /**
   * Check if any of the recommendations for this issue are available to the user
   */
  hasAvailableRecommendations(columnName: string, issueId: string): boolean {
    const itemId = `${columnName}_${issueId}`;
    const finding = this.findingsMap.get(itemId);
    
    if (!finding?.recommendations || finding.recommendations.length === 0) {
      return false;
    }
    
    // If catalog not loaded yet, assume all recommendations are available
    if (!this.goalCatalogLoaded) {
      return true;
    }
    
    // Check if at least one recommendation exists in the catalog and is available
    return finding.recommendations.some(recommendationKey => 
      this.goalCatalog.some(goal => goal.goalKey === recommendationKey && (goal.isAvailable ?? true) === true)
    );
  }

  /**
   * Check if issue has recommendations but none are available (disabled state)
   */
  hasUnavailableRecommendations(columnName: string, issueId: string): boolean {
    const itemId = `${columnName}_${issueId}`;
    const finding = this.findingsMap.get(itemId);
    
    if (!finding?.recommendations || finding.recommendations.length === 0) {
      return false;
    }
    
    // If catalog not loaded yet, don't show as unavailable
    if (!this.goalCatalogLoaded) {
      return false;
    }

    // Check if ALL recommendations are unavailable.
    // A recommendation is considered available only when a matching goal exists
    // and that goal's `isAvailable` is not explicitly false. If `isAvailable`
    // is undefined, treat it as available (default true).
    return finding.recommendations.every(recommendationKey => {
      const goal = this.goalCatalog.find(g => g.goalKey === recommendationKey);
      // No goal entry -> unavailable
      if (!goal) return true;
      // If isAvailable is undefined, default to true
      const available = (goal.isAvailable ?? true) === true;
      return !available; // return true when unavailable
    });
  }

  /**
   * Recompute the total number of issues that have available recommendations
   * and update `totalIssueCount` used by the floating basket.
   */
  private updateTotalIssueCount(): void {
    let count = 0;
    this.tableFields.forEach(field => {
      if (field.issues && field.issues.length > 0) {
        field.issues.forEach(issue => {
          const itemId = `${field.field}_${issue.id}`;
          const finding = this.findingsMap.get(itemId);
          if (finding?.recommendations && finding.recommendations.length > 0) {
            if (this.hasAvailableRecommendations(field.field, issue.id)) {
              count++;
            }
          }
        });
      }
    });
    this.totalIssueCount = count;
  }

  /**
   * Handler when user clicks Upgrade for an unavailable recommendation
   */
  onUpgradeClick(columnName: string, issueId: string): void {
    // Navigate to the registration/upgrade flow and include context
  }

  /**
   * Load goal catalog to determine which recommendations are available
   */
  private loadGoalCatalog(): void {
    this.dataService.getGoalCatalog().subscribe({
      next: (catalog) => {
        this.goalCatalog = catalog;
        this.goalCatalogLoaded = true;
        console.log('Goal catalog loaded:', catalog.length, 'goals available');
        // Recompute counts now that availability information is known
        this.updateTotalIssueCount();
      },
      error: (error) => {
        console.error('Failed to load goal catalog:', error);
        // Don't show error to user - this is a background operation
        // If catalog fails to load, checkboxes will remain enabled
        this.goalCatalogLoaded = false;
        // Still recompute so UI reflects fallback assumptions
        this.updateTotalIssueCount();
      }
    });
  }

  onViewSelectedIssues(): void {
    console.log('View selected issues:', this.basketItems);
    // TODO: Implement view/action dialog for selected issues
    this.snackBar.open(`${this.basketItems.length} issues selected for fixing`, '×', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['info-snackbar']
    });
  }

  onClearBasket(): void {
    this.basketItems = [];
    this.selectedIssueIds.clear();
    this.snackBar.open('All issues deselected', '×', {
      duration: 2000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['info-snackbar']
    });
  }

  onSelectAll(): void {
    this.autoSelectAllIssues();
  }

  private autoSelectAllIssues(): void {
    this.basketItems = [];
    this.selectedIssueIds.clear();
    // totalIssueCount is computed separately; do not mutate here

    this.tableFields.forEach(field => {
      if (field.issues && field.issues.length > 0) {
        field.issues.forEach(issue => {
          const itemId = `${field.field}_${issue.id}`;

          // Get the finding to access recommendations
          const finding = this.findingsMap.get(itemId);

          // Only auto-select issues that have recommendations and at least
          // one available recommendation according to the loaded catalog.
          if (finding?.recommendations && finding.recommendations.length > 0
              && this.hasAvailableRecommendations(field.field, issue.id)) {
            this.selectedIssueIds.add(itemId);

            this.basketItems.push({
              id: itemId,
              columnName: field.field,
              issueTitle: issue.title,
              severity: issue.severity,
              recommendations: finding.recommendations
            });
          }
        });
      }
    });
    // Ensure displayed total is accurate after selection
    this.updateTotalIssueCount();
  }

  /**
   * Determine whether to show the Selected Fixes toolbar (floating basket)
   * Don't show for reopened uploaded file profiles where the file is no longer in memory
   */
  shouldShowBasket(): boolean {
    // Don't show if this is a reopened uploaded file profile without the file data
    // (dataSourceId is '0' or invalid, and we don't have the uploaded file info)
    if ((!this.dataSourceId || this.dataSourceId === '0') && !this.uploadedFileInfo) {
      return false;
    }
    return true;
  }

  /**
   * Start a cleaning workflow based on selected issues
   */
  onStartCleaningWorkflow(): void {
    if (this.basketItems.length === 0) {
      this.snackBar.open('Please select at least one issue to fix', '×', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Show confirmation dialog first
    this.dialogService.openConfirmDialog(
      'Cleaning Workflow - Getting Started',
      `The workflow that will be created is largely based on the structural issues identified by the data assessment. To correct specific types of data (such as dates, emails, addresses, numeric values, geo location, web etc). please add the appropriate goal(s) from the goals library`,
      'Continue',
      'Cancel'
    ).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      // Set initial workflow state
      this.loading = true;
      this.creatingWorkflow = true;
      this.workflowProgress = null;
      this.loadingMessage = 'Preparing Workflow...';
      
      // Close the sidebar and clear profiling progress indicators
      this.isRightSidebarCollapsed = true;
      this.profilingCurrentStep = null;
      this.profilingTotalSteps = null;
      this.fetchingProfilingResults = false;

      // Check if this is an uploaded file (dataSourceId === '0')
      const isUploadedFile = this.dataSourceId === '0';

      if (isUploadedFile) {
        // Ask user if they want to create it as a datasource
        this.dialogService.openConfirmDialog(
          'Save as Data Source',
          'This data is currently an uploaded file. Would you like to save it as a Data Source in DQP before creating the cleaning workflow? This will make it easier to reuse in future workflows.',
          'Yes, Save',
          'No, Continue'
        ).subscribe(shouldCreateDataSource => {
          if (shouldCreateDataSource) {
            this.createDataSourceAndProject();
          } else if (shouldCreateDataSource === false) {
            // User chose to continue without creating datasource - reset state
            this.loading = false;
            this.creatingWorkflow = false;
            this.workflowProgress = null;
            this.snackBar.open('Cannot create workflow without a data source. Please save your data first.', '×', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          } else {
            // User cancelled - reset state
            this.loading = false;
            this.creatingWorkflow = false;
            this.workflowProgress = null;
          }
        });
      } else {
        // Already have a datasource, proceed directly
        this.createProjectWithGoals(this.dataSourceId!);
      }
    });
  }

  /**
   * Create a datasource from uploaded file, then create project
   */
  private createDataSourceAndProject(): void {
    const uploadedFileInfo = this.uploadedFileInfo;
    const uploadedFile = uploadedFileInfo?.originalFile;
    if (!uploadedFileInfo || !uploadedFile) {
      this.loading = false;
      this.creatingWorkflow = false;
      this.workflowProgress = null;
      this.snackBar.open('No uploaded file information available', '×', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.loading = true;
    this.loadingMessage = 'Checking Data Source Name...';

    // Upload file as data source (creates datasource and uploads in one call)
    const dataSourceName = uploadedFileInfo.fileName || 'Uploaded File';
    
    // Check if datasource name already exists
    this.dataService.checkDataSourceExists(dataSourceName).subscribe({
      next: (existsResult) => {
        if (existsResult.exists) {
          // Datasource name exists - fetch datasources to get the ID
          this.loadingMessage = 'Checking existing data sources...';
          this.dataService.getDataSourceList().subscribe({
            next: (dataSources) => {
              // Find the existing datasource to get its ID
              const existingDs = dataSources.find(ds => 
                ds.dataSourceName.toLowerCase() === dataSourceName.toLowerCase()
              );
              
              // Stop loading for dialog
              this.loading = false;
              this.creatingWorkflow = false;
              this.workflowProgress = null;
              
              const dialogRef = this.dialogService.openDialog(DatasourceNameConflictDialogComponent, {
                width: '700px',
                maxWidth: '90vw',
                data: {
                  datasourceName: dataSourceName,
                  dataSourceId: existingDs?.dataSourceId
                } as DatasourceConflictData
              });

              dialogRef.afterClosed().subscribe((result: DatasourceConflictResult) => {
                if (!result || result.action === 'cancel') {
                  // User cancelled - do nothing
                  return;
                }

                // Resume workflow creation
                this.loading = true;
                this.creatingWorkflow = true;
                this.workflowProgress = null;

                if (!existingDs?.dataSourceId && result.action !== 'rename') {
                  this.loading = false;
                  this.creatingWorkflow = false;
                  this.snackBar.open('The existing data source could not be found. Please try again.', '×', {
                    duration: 5000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                  });
                } else if (result.action === 'replace' && existingDs?.dataSourceId) {
                  // Replace the existing datasource
                  this.loadingMessage = 'Replacing Data Source...';
                  this.dataService.replaceFileDataSourceWithProgress(existingDs.dataSourceId, uploadedFile).subscribe({
                    next: (event: any) => {
                      if (event.type === HttpEventType.Response) {
                        console.log('Datasource replaced successfully');
                        this.createProjectWithGoals(existingDs.dataSourceId!);
                      }
                    },
                    error: (error) => {
                      console.error('Error replacing datasource:', error);
                      this.loading = false;
                      this.creatingWorkflow = false;
                      this.workflowProgress = null;
                      // Don't display error message for 402 Payment Required - already handled by interceptor
                      if (error.status !== 402) {
                        this.snackBar.open('Failed to replace data source', '×', {
                          duration: 5000,
                          horizontalPosition: 'center',
                          verticalPosition: 'top',
                          panelClass: ['error-snackbar']
                        });
                      }
                    }
                  });
                } else if (result.action === 'use-existing' && existingDs?.dataSourceId) {
                  // Use the existing datasource
                  this.loadingMessage = 'Using Existing Data Source...';
                  this.createProjectWithGoals(existingDs.dataSourceId);
                } else if (result.action === 'rename' && result.newName) {
                  // Create new datasource with new name
                  this.loadingMessage = 'Creating Data Source...';
                  this.dataService.uploadFileDataSourceWithProgress(
                    uploadedFile,
                    result.newName
                  ).subscribe({
                    next: (event: any) => {
                      if (event.type === HttpEventType.Response) {
                        const dataSourceId = this.getUploadedDataSourceId(event.body);
                        console.log('Data source created with new name:', dataSourceId);
                        this.createProjectWithGoals(dataSourceId);
                      }
                    },
                    error: (error: any) => {
                      console.error('Error creating datasource:', error);
                      this.loading = false;
                      this.creatingWorkflow = false;
                      this.workflowProgress = null;
                      // Don't display error message for 402 Payment Required - already handled by interceptor
                      if (error.status !== 402) {
                        this.snackBar.open('Failed to create data source', '×', {
                          duration: 5000,
                          horizontalPosition: 'center',
                          verticalPosition: 'top',
                          panelClass: ['error-snackbar']
                        });
                      }
                    }
                  });
                }
              });
            },
            error: (error) => {
              console.error('Error fetching datasources:', error);
              this.loading = false;
              this.creatingWorkflow = false;
              this.workflowProgress = null;
              this.snackBar.open('Failed to check existing data sources', '×', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          });
        } else {
          // Name doesn't exist, proceed with creation
          this.loadingMessage = 'Creating Data Source...';
          this.dataService.uploadFileDataSourceWithProgress(
            uploadedFile,
            dataSourceName
          ).subscribe({
            next: (event: any) => {
              if (event.type === HttpEventType.Response) {
                const dataSourceId = this.getUploadedDataSourceId(event.body);
                console.log('Data source created and file uploaded:', dataSourceId);
                this.createProjectWithGoals(dataSourceId);
              }
            },
            error: (error: any) => {
              console.error('Error creating datasource:', error);
              this.loading = false;
              this.creatingWorkflow = false;
              this.workflowProgress = null;
              this.snackBar.open('Failed to create data source', '×', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          });
        }
      },
      error: (error) => {
        console.error('Error checking datasource name:', error);
        this.loading = false;
        this.creatingWorkflow = false;
        this.workflowProgress = null;
        this.snackBar.open('Failed to check data source name', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  private getUploadedDataSourceId(response: string): string {
    const trimmedResponse = response.trim();
    if (!trimmedResponse.startsWith('{')) {
      return response;
    }

    try {
      return JSON.parse(trimmedResponse).dataSourceId;
    } catch {
      return response;
    }
  }

  /**
   * Create a cleaning project with goals based on issue recommendations
   */
  private createProjectWithGoals(dataSourceId: string): void {
    this.loadingMessage = 'Creating Cleaning Workflow...';

    // Generate project name with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const projectName = `Cleaning Workflow - ${timestamp}`;

    // Create project with default settings (similar to dashboard)
    const projectCreate: ProjectCreate = {
      projectName: projectName,
      description: `Auto-generated cleaning workflow for ${this.basketItems.length} detected issues`
    };

    this.dataService.createProject(projectCreate).subscribe({
      next: (projectId) => {
        console.log('Project created:', projectId);
        
        // Assign datasource to project
        this.dataService.assignDataSourceToProject(projectId.toString(), dataSourceId).subscribe({
          next: () => {
            console.log('Datasource assigned to project');
            // Fetch goals and create them
            this.fetchGoalsAndCreateForProject(projectId.toString());
          },
          error: (error) => {
            console.error('Error assigning datasource:', error);
            this.loading = false;
            this.creatingWorkflow = false;
            this.workflowProgress = null;
            // Don't display error message for 402 Payment Required - already handled by interceptor
            if (error.status !== 402) {
              this.snackBar.open('Project created but failed to assign data source', '×', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
            // Navigate anyway
            this.router.navigate(['/project-canvas', projectId]);
          }
        });
      },
      error: (error) => {
        console.error('Error creating project:', error);
        this.loading = false;
        this.creatingWorkflow = false;
        this.workflowProgress = null;
        // Don't display error message for 402 Payment Required - already handled by interceptor
        if (error.status !== 402) {
          this.snackBar.open('Failed to create project', '×', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      }
    });
  }

  /**
   * Fetch available goals and create matching ones for the project
   */
  private fetchGoalsAndCreateForProject(projectId: string): void {
    this.loadingMessage = 'Configuring Goals...';
    
    // Enable workflow creation UI
    this.creatingWorkflow = true;
    this.workflowProgress = null; // Indeterminate initially

    this.dataService.getGoalCatalog().subscribe({
      next: (goalCatalog) => {
        console.log('Goal catalog fetched:', goalCatalog);
        
        // Build goal creation requests from recommendations
        const goalRequests = this.buildGoalCreationRequests(goalCatalog);
        
        if (goalRequests.length === 0) {
          console.log('No matching goals found for recommendations');
          this.loading = false;
          this.creatingWorkflow = false;
          this.workflowProgress = null;
          
          this.router.navigate(['/project-canvas', projectId]);
          return;
        }

        // Initialize progress with total count
        this.workflowProgress = { current: 0, total: goalRequests.length };

        // Create goals sequentially
        this.createGoalsSequentially(goalRequests, projectId, 0);
      },
      error: (error) => {
        console.error('Error fetching goal catalog:', error);
        this.loading = false;
        this.creatingWorkflow = false;
        this.workflowProgress = null;
        this.snackBar.open('Project created but failed to configure goals', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        this.router.navigate(['/project-canvas', projectId]);
      }
    });
  }

  /**
   * Create goals sequentially to provide better progress feedback
   */
  private createGoalsSequentially(
    requests: Array<{ catalogGoalId: string; goalKey: string; goalName: string; description: string; columns: string[] }>,
    projectId: string,
    currentIndex: number
  ): void {
    // Base case: all goals created
    if (currentIndex >= requests.length) {
      this.loading = false;
      this.creatingWorkflow = false;
      
      this.snackBar.open(
        `Project created with ${this.workflowProgress?.current || 0} goal${this.workflowProgress?.current !== 1 ? 's' : ''}!`,
        '×',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        }
      );
      
      // Reset progress and navigate
      this.workflowProgress = null;
      this.router.navigate(['/project-canvas', projectId]);
      return;
    }

    const request = requests[currentIndex];
    
    // Fetch task catalog for this goal
    this.dataService.getTasksForGoal(request.catalogGoalId).pipe(
      switchMap(taskCatalog => {
        // Build goal with tasks from catalog
        const goalToCreate = this.buildGoalFromTaskCatalog(request, taskCatalog, projectId);
        
        // Skip this goal if it has no tasks
        if (!goalToCreate.Steps || goalToCreate.Steps.length === 0) {
          console.log(`Skipping goal "${request.goalName}" - no tasks to include`);
          return of(null);
        }
        
        return this.dataService.createGoal(goalToCreate);
      }),
      catchError(error => {
        console.error(`Error creating goal ${request.goalName}:`, error);
        return of(null); // Continue with next goal even if this one fails
      })
    ).subscribe({
      next: (result) => {
        // Update progress
        if (this.workflowProgress && result !== null) {
          this.workflowProgress = {
            current: this.workflowProgress.current + 1,
            total: this.workflowProgress.total
          };
        }
        
        // Create next goal
        this.createGoalsSequentially(requests, projectId, currentIndex + 1);
      },
      error: (error) => {
        console.error(`Failed to create goal at index ${currentIndex}:`, error);
        // Continue with next goal even on error
        this.createGoalsSequentially(requests, projectId, currentIndex + 1);
      }
    });
  }

  /**
   * Build goal creation requests from issue recommendations
   */
  private buildGoalCreationRequests(goalCatalog: GoalCatalogGet[]): Array<{
    catalogGoalId: string;
    goalKey: string;
    goalName: string;
    description: string;
    columns: string[];
  }> {
    const requests: Array<any> = [];
    const createdGoalKeys = new Set<string>();
    const goalKeyToColumns = new Map<string, Set<string>>();
    const notFoundGoalKeys: string[] = [];

    // Collect all recommendations and their columns
    this.basketItems.forEach(item => {
      if (item.recommendations && item.recommendations.length > 0) {
        item.recommendations.forEach(recommendation => {
          if (!goalKeyToColumns.has(recommendation)) {
            goalKeyToColumns.set(recommendation, new Set());
          }
          goalKeyToColumns.get(recommendation)!.add(item.columnName);
        });
      }
    });

    // Log identified unique goals
    console.log('=== IDENTIFIED UNIQUE GOALS FROM RECOMMENDATIONS ===');
    console.log(`Total unique goal keys: ${goalKeyToColumns.size}`);
    goalKeyToColumns.forEach((columns, goalKey) => {
      console.log(`  - Goal Key: "${goalKey}"`);
      console.log(`    Columns: [${Array.from(columns).join(', ')}]`);
    });
    console.log('');

    // Log available goal catalog keys for comparison
    console.log('=== AVAILABLE GOAL TEMPLATES IN CATALOG ===');
    console.log(`Total templates: ${goalCatalog.length}`);
    goalCatalog.forEach(goal => {
      console.log(`  - Goal Key: "${goal.goalKey}" | Name: "${goal.name}" | ID: ${goal.goalId}`);
    });
    console.log('');

    // For each unique recommendation, find matching goal and ensure it's available
    console.log('=== MATCHING GOALS WITH TEMPLATES ===');
    goalKeyToColumns.forEach((columns, recommendationKey) => {
      const matchingGoal = goalCatalog.find(goal => goal.goalKey === recommendationKey);

      // Treat missing templates or explicitly unavailable goals as not found
      const isGoalAvailable = matchingGoal ? (matchingGoal.isAvailable ?? true) === true : false;

      if (matchingGoal && isGoalAvailable && !createdGoalKeys.has(recommendationKey)) {
        createdGoalKeys.add(recommendationKey);

        requests.push({
          catalogGoalId: matchingGoal.goalId,
          goalKey: matchingGoal.goalKey,
          goalName: matchingGoal.name,
          description: matchingGoal.description || '',
          columns: Array.from(columns)
        });

        console.log(`  ✅ MATCHED: "${recommendationKey}"`);
        console.log(`     Template: "${matchingGoal.name}" (ID: ${matchingGoal.goalId})`);
        console.log(`     Columns: [${Array.from(columns).join(', ')}]`);
      } else {
        notFoundGoalKeys.push(recommendationKey);
        if (!matchingGoal) {
          console.log(`  ❌ NOT FOUND: "${recommendationKey}"`);
        } else {
          console.log(`  ❌ NOT AVAILABLE: "${recommendationKey}" (template exists but is not available)`);
        }
        console.log(`     Columns: [${Array.from(columns).join(', ')}]`);
      }
    });
    console.log('');

    // Summary
    console.log('=== GOAL MATCHING SUMMARY ===');
    console.log(`Total goals identified: ${goalKeyToColumns.size}`);
    console.log(`Successfully matched: ${requests.length}`);
    console.log(`Not found in catalog: ${notFoundGoalKeys.length}`);
    if (notFoundGoalKeys.length > 0) {
      console.log(`Goal keys not found: [${notFoundGoalKeys.join(', ')}]`);
    }
    console.log('===================================');
    console.log('');

    return requests;
  }

  /**
   * Build a goal with tasks from the task catalog
   */
  private buildGoalFromTaskCatalog(
    request: { catalogGoalId: string; goalKey: string; goalName: string; description: string; columns: string[] },
    taskCatalog: TaskCatalogGet[],
    projectId: string
  ): any {
    console.log(`\n=== BUILDING GOAL: "${request.goalName}" (${request.goalKey}) ===`);
    console.log(`Goal ID: ${request.catalogGoalId}`);
    console.log(`Columns: [${request.columns.join(', ')}]`);
    console.log(`Total tasks in catalog: ${taskCatalog.length}`);

    // Sort tasks by order
    const sortedTasks = taskCatalog.sort((a, b) => (a.taskOrder || 0) - (b.taskOrder || 0));

    // Build tasks array - enable mandatory and required-by-default tasks
    const tasksToSave = sortedTasks
      .map(task => {
        // Check if task is mandatory or required by default
        let isRequired = false;
        try {
          if (task.taskMetadata) {
            const metadata = JSON.parse(task.taskMetadata);
            isRequired = metadata.required === true || metadata.mandatory === true;
          }
        } catch (e) {
          console.warn('Failed to parse task metadata:', e);
        }

        // Parse default parameters from metadata
        let params: any = {};
        try {
          if (task.taskMetadata) {
            const metadata = JSON.parse(task.taskMetadata);
            if (metadata.params?.properties) {
              // Extract default values from parameter schema
              Object.keys(metadata.params.properties).forEach(key => {
                const prop = metadata.params.properties[key];
                if (prop.default !== undefined) {
                  params[key] = prop.default;
                }
              });
            }
          }
        } catch (e) {
          console.warn('Failed to parse task parameters:', e);
        }

        return {
          task,
          isRequired,
          params
        };
      })
      .filter(({ isRequired, task }) => {
        if (!isRequired) {
          console.log(`  ⏭️  SKIPPED: "${task.name}" (${task.taskKey}) - not required/mandatory`);
        }
        return isRequired;
      })
      .map(({ task, params }) => {
        console.log(`  ✅ INCLUDED: "${task.name}" (${task.taskKey})`);
        console.log(`     Parameters: ${Object.keys(params).length > 0 ? JSON.stringify(params) : '(none)'}`);
        return {
          task_id: task.taskKey,
          params: params,
          select: {
            columns: request.columns
          }
        };
      });

    console.log(`Tasks to save: ${tasksToSave.length}/${taskCatalog.length}`);
    console.log(`==========================================\n`);

    // Create goal with PascalCase properties (API requires this)
    return {
      ProjectId: projectId,
      GoalCatalogId: request.catalogGoalId,
      GoalName: request.goalName,
      Description: request.description,
      IsActive: true,
      Order: 0,
      Steps: tasksToSave
    };
  }
}

